import React, { useEffect, useState, useRef, useCallback, startTransition, useMemo } from 'react';
import { Recycle, HardDrive, SlidersHorizontal, CheckCircle, ChevronRight, Smartphone, Battery, Shield, Zap, Star, Wifi, Cpu, Layers, Check, Lightbulb, ListChecks, Camera, Package } from 'lucide-react';
import styled, { keyframes } from 'styled-components';
import { getTranslation, Language } from '../../translations';
import { useLanguage } from '../Header';
import { getPublicQuestionsWithOptions, getPublicDeviceModelByKey, getPublicStorageOptionsByModelKey } from '../../lib/publicDataAccess';
import { formatDeviceNameMeta, resolveDeviceDisplayName, splitDeviceDisplayName } from '../../utils/deviceDisplayName';
import StorageStep from './StorageStep';
import QuestionStep from './QuestionStep';
import LegacyQuestionStep from './LegacyQuestionStep';
import BatteryHealthModal from './BatteryHealthModal';
import PriceDisplay from './PriceDisplay';
import NavigationButtons from './NavigationButtons';
import { Question, type DynamicQuestion, type DeviceSpecificationsProps } from './types';
import { useDeviceQuestions } from './hooks/useDeviceQuestions';
import { useAnswers } from './hooks/useAnswers';
import { usePriceCalculation } from './hooks/usePriceCalculation';
import { buildOfferRequestPayload, buildSellSpecs } from './utils/buildOfferPayload';

// Clean, minimal animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

// Shimmer animation for pleasant loading
const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const attentionPulse = keyframes`
  0%, 100% { transform: scale(1); box-shadow: 0 2px 8px rgba(15, 23, 42, 0.2); }
  50% { transform: scale(1.04); box-shadow: 0 0 0 8px rgba(17, 24, 39, 0.15), 0 6px 20px rgba(15, 23, 42, 0.3); }
`;

// Phone illustration: slide in from the far left when the question mounts
const phoneSlideIn = keyframes`
  from { transform: translateX(-220px); opacity: 0; }
  to { transform: translateX(-40px); opacity: 1; }
`;

const phoneSlideInWide = keyframes`
  from { transform: translateX(-260px); opacity: 0; }
  to { transform: translateX(-64px); opacity: 1; }
`;

const LoadingDots = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #94a3b8;
    animation: loadingDot 1.2s infinite ease-in-out;
  }
  .dot:nth-child(2) { animation-delay: .15s; }
  .dot:nth-child(3) { animation-delay: .3s; }
  @keyframes loadingDot { 0%, 80%, 100% { transform: scale(.8); opacity: .5 } 40% { transform: scale(1); opacity: 1 } }
`;



// Question loading skeleton
const SkeletonBlock = styled.div`
  background: #e2e8f0;
  background-image: linear-gradient(90deg, #e2e8f0 0%, #f1f5f9 40%, #e2e8f0 80%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite linear;
  border-radius: 10px;

  .dark & {
    background: #1e293b;
    background-image: linear-gradient(90deg, #1e293b 0%, #334155 40%, #1e293b 80%);
    background-size: 200% 100%;
  }
`;

const QuestionSkeleton = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  padding: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const SkeletonImageArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const SkeletonDeviceImage = styled(SkeletonBlock)`
  width: 140px;
  height: 200px;
  border-radius: 20px;

  @media (max-width: 640px) {
    width: 100px;
    height: 140px;
  }
`;

const SkeletonOptionsArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SkeletonTitle = styled(SkeletonBlock)`
  width: 70%;
  height: 1.5rem;
  margin-bottom: 0.5rem;
`;

const SkeletonSubtitle = styled(SkeletonBlock)`
  width: 50%;
  height: 1rem;
  margin-bottom: 1.5rem;
`;

const SkeletonOptionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
`;

const SkeletonOption = styled(SkeletonBlock)`
  height: 72px;
  border-radius: 14px;
`;

const PlaceholderCard = styled.div`
  text-align: center;
  @media (max-width: 640px) {
    text-align: left;
    padding-left: 0.25rem;
    padding-right: 0.25rem;
  }
  padding: 1.25rem 1.5rem;
  background: #fbfdff;
  border: 2px dashed #d8e1ee;
  border-radius: 14px;
  width: 100%;
  color: #64748b;
  font-weight: 700;
`;

const StorageIcon = () => (<HardDrive size={20} strokeWidth={1.75} />);

// Legacy question icon components moved to LegacyQuestionStep component

// Map iPhone models to their available storage capacities
const getIphoneStorageValues = (modelNameRaw: string): string[] => {
  const name = (modelNameRaw || '').toLowerCase();

  // iPhone 18 series (2026): Pro and Pro Max start at 256GB and go up to 2TB
  if (name.includes('iphone 18 pro max') || name.includes('iphone 18 pro')) {
    return ['256GB', '512GB', '1TB', '2TB'];
  }

  // iPhone 17 series
  if (name.includes('iphone 17 pro max') || name.includes('iphone 17 pro')) {
    return ['128GB', '256GB', '512GB', '1TB'];
  }
  if (name.includes('iphone 17 air') || name === 'iphone 17') {
    return ['128GB', '256GB', '512GB', '1TB'];
  }

  // iPhone 16 series
  if (name.includes('iphone 16 pro max') || name.includes('iphone 16 pro')) {
    return ['128GB', '256GB', '512GB', '1TB'];
  }
  if (name.includes('iphone 16 plus') || name === 'iphone 16' || name.includes('iphone 16e')) {
    return ['128GB', '256GB', '512GB'];
  }

  // iPhone 15 series
  if (name.includes('iphone 15 pro max') || name.includes('iphone 15 pro')) {
    return ['128GB', '256GB', '512GB', '1TB'];
  }
  if (name.includes('iphone 15 plus') || name === 'iphone 15') {
    return ['128GB', '256GB', '512GB'];
  }

  // iPhone 14 series
  if (name.includes('iphone 14 pro max') || name.includes('iphone 14 pro')) {
    return ['128GB', '256GB', '512GB', '1TB'];
  }
  if (name.includes('iphone 14 plus') || name === 'iphone 14') {
    return ['128GB', '256GB', '512GB'];
  }

  // iPhone 13 series
  if (name.includes('iphone 13 pro max') || name.includes('iphone 13 pro')) {
    return ['128GB', '256GB', '512GB', '1TB'];
  }
  if (name.includes('iphone 13') || name.includes('iphone 13 mini')) {
    return ['128GB', '256GB', '512GB'];
  }

  // iPhone 12 series
  if (name.includes('iphone 12 pro max') || name.includes('iphone 12 pro')) {
    return ['128GB', '256GB', '512GB'];
  }
  if (name.includes('iphone 12') || name.includes('iphone 12 mini')) {
    return ['64GB', '128GB', '256GB'];
  }

  // iPhone 11 series
  if (name.includes('iphone 11 pro max') || name.includes('iphone 11 pro')) {
    return ['64GB', '256GB', '512GB'];
  }
  if (name === 'iphone 11') {
    return ['64GB', '128GB', '256GB'];
  }

  // iPhone X series
  if (name.includes('iphone xs max') || name.includes('iphone xs')) {
    return ['64GB', '256GB', '512GB'];
  }
  if (name.includes('iphone xr')) {
    return ['64GB', '128GB', '256GB'];
  }
  if (name === 'iphone x') {
    return ['64GB', '256GB'];
  }

  // iPhone 8 series
  if (name.includes('iphone 8 plus') || name === 'iphone 8') {
    return ['64GB', '128GB', '256GB'];
  }

  // iPhone 7 series
  if (name.includes('iphone 7 plus') || name === 'iphone 7') {
    return ['32GB', '128GB', '256GB'];
  }

  // iPhone SE
  if (name.includes('iphone se')) {
    return ['64GB', '128GB', '256GB'];
  }

  // Fallback
  return ['128GB', '256GB', '512GB'];
};

// Map Samsung Galaxy models to their available storage capacities
const getSamsungStorageValues = (modelNameRaw: string): string[] => {
  const name = (modelNameRaw || '').toLowerCase();
  // Normalize to handle formats like "Samsung Galaxy S 25 Plus", "S-25+", etc.
  const key = name.replace(/\s+|\-/g, '');

  // Try robust regex parsing for S-series
  const sMatch = name.match(/s\s*([0-9]{1,2})\s*(ultra|plus|fe)?/i);
  if (sMatch) {
    const series = parseInt(sMatch[1], 10);
    const variant = (sMatch[2] || '').toLowerCase();
    const bySeries: Record<number, Record<string, string[]>> = {
      25: {
        ultra: ['256GB', '512GB', '1TB'],
        plus: ['256GB', '512GB'],
        base: ['128GB', '256GB', '512GB']
      },
      24: {
        ultra: ['256GB', '512GB', '1TB'],
        plus: ['256GB', '512GB'],
        base: ['128GB', '256GB', '512GB']
      },
      23: {
        ultra: ['256GB', '512GB', '1TB'],
        plus: ['256GB', '512GB'],
        base: ['128GB', '256GB', '512GB']
      },
      22: {
        ultra: ['128GB', '256GB', '512GB', '1TB'],
        plus: ['128GB', '256GB'],
        base: ['128GB', '256GB']
      },
      21: {
        ultra: ['128GB', '256GB', '512GB'],
        plus: ['128GB', '256GB'],
        fe: ['128GB', '256GB'],
        base: ['128GB', '256GB']
      },
      20: {
        ultra: ['128GB', '256GB', '512GB'],
        plus: ['128GB', '256GB', '512GB'],
        fe: ['128GB', '256GB'],
        base: ['128GB']
      },
      10: {
        plus: ['128GB', '512GB', '1TB'],
        e: ['128GB', '256GB'],
        base: ['128GB', '512GB']
      },
      9: {
        plus: ['64GB', '128GB', '256GB'],
        base: ['64GB', '128GB', '256GB']
      },
      8: {
        base: ['64GB']
      }
    };
    const seriesMap = bySeries[series as keyof typeof bySeries];
    if (seriesMap) {
      if (variant && seriesMap[variant]) return seriesMap[variant];
      return seriesMap.base || ['128GB', '256GB'];
    }
  }

  // Galaxy S25
  if (key.includes('s25ultra')) return ['256GB', '512GB', '1TB'];
  if (key.includes('s25plus')) return ['256GB', '512GB'];
  if (key.includes('s25')) return ['128GB', '256GB', '512GB'];

  // Galaxy S24
  if (key.includes('s24ultra')) return ['256GB', '512GB', '1TB'];
  if (key.includes('s24plus')) return ['256GB', '512GB'];
  if (key.includes('s24')) return ['128GB', '256GB', '512GB'];

  // Galaxy S23
  if (key.includes('s23ultra')) return ['256GB', '512GB', '1TB'];
  if (key.includes('s23plus')) return ['256GB', '512GB'];
  if (key.includes('s23')) return ['128GB', '256GB', '512GB'];

  // Galaxy S22
  if (key.includes('s22ultra')) return ['128GB', '256GB', '512GB', '1TB'];
  if (key.includes('s22plus')) return ['128GB', '256GB'];
  if (key.includes('s22')) return ['128GB', '256GB'];

  // Galaxy S21
  if (key.includes('s21ultra')) return ['128GB', '256GB', '512GB'];
  if (key.includes('s21plus')) return ['128GB', '256GB'];
  if (key.includes('s21fe')) return ['128GB', '256GB'];
  if (key.includes('s21')) return ['128GB', '256GB'];

  // Galaxy S20
  if (key.includes('s20ultra')) return ['128GB', '256GB', '512GB'];
  if (key.includes('s20plus')) return ['128GB', '256GB', '512GB'];
  if (key.includes('s20fe')) return ['128GB', '256GB'];
  if (key.includes('s20')) return ['128GB'];

  // Galaxy S10
  if (key.includes('s10plus')) return ['128GB', '512GB', '1TB'];
  if (key.includes('s10e')) return ['128GB', '256GB'];
  if (key.includes('s10')) return ['128GB', '512GB'];

  // Galaxy S9
  if (key.includes('s9plus')) return ['64GB', '128GB', '256GB'];
  if (key.includes('s9')) return ['64GB', '128GB', '256GB'];

  // Galaxy S8
  if (key.includes('s8')) return ['64GB'];

  // Galaxy Note
  if (key.includes('note20ultra')) return ['128GB', '256GB', '512GB'];
  if (key.includes('note20')) return ['128GB', '256GB'];
  if (key.includes('note10lite')) return ['128GB'];
  if (key.includes('note10')) return ['256GB'];
  if (key.includes('note9')) return ['128GB', '512GB'];
  if (key.includes('note8')) return ['64GB', '128GB', '256GB'];

  // Galaxy Z (Fold/Flip)
  if (key.includes('fold6')) return ['256GB', '512GB', '1TB'];
  if (key.includes('flip6')) return ['256GB', '512GB'];
  if (key.includes('fold5')) return ['256GB', '512GB', '1TB'];
  if (key.includes('flip5')) return ['256GB', '512GB'];
  if (key.includes('fold4')) return ['256GB', '512GB', '1TB'];
  if (key.includes('flip4')) return ['128GB', '256GB', '512GB'];
  if (key.includes('fold3')) return ['256GB', '512GB'];
  if (key.includes('flip3')) return ['128GB', '256GB'];
  if (key.includes('fold2')) return ['256GB', '512GB'];
  if (key.includes('flip')) return ['256GB'];

  // Fallback
  return ['128GB', '256GB'];
};

// Map iPad models to their available storage capacities
const getIPadStorageValues = (modelNameRaw: string): string[] => {
  const name = (modelNameRaw || '').toLowerCase();
  const key = name.replace(/\s+|\-/g, '');

  // iPad Pro 2024 (M4)
  if (key.includes('ipadpro13m4') || key.includes('ipadpro137') || key.includes('ipadpro11-5') || key.includes('ipadpro115')) {
    return ['256GB', '512GB', '1TB', '2TB'];
  }
  // iPad Pro 12.9"
  if (key.includes('ipadpro1296')) return ['128GB', '256GB', '512GB', '1TB', '2TB']; // M2
  if (key.includes('ipadpro1295')) return ['128GB', '256GB', '512GB', '1TB', '2TB']; // M1
  if (key.includes('ipadpro1294')) return ['128GB', '256GB', '512GB', '1TB']; // A12Z
  if (key.includes('ipadpro1293')) return ['64GB', '256GB', '512GB', '1TB']; // A12X
  if (key.includes('ipadpro1292')) return ['64GB', '256GB', '512GB']; // A10X
  if (key.includes('ipadpro1291')) return ['32GB', '128GB', '256GB']; // A9X (refresh added 256GB)

  // iPad Pro 11"
  if (key.includes('ipadpro115')) return ['256GB', '512GB', '1TB', '2TB']; // M4 handled above too
  if (key.includes('ipadpro114')) return ['128GB', '256GB', '512GB', '1TB', '2TB']; // M2
  if (key.includes('ipadpro113')) return ['128GB', '256GB', '512GB', '1TB', '2TB']; // M1
  if (key.includes('ipadpro112')) return ['128GB', '256GB', '512GB', '1TB']; // A12Z
  if (key.includes('ipadpro111')) return ['64GB', '256GB', '512GB', '1TB']; // A12X

  // iPad Pro 10.5" & 9.7"
  if (key.includes('ipadpro105')) return ['64GB', '256GB', '512GB'];
  if (key.includes('ipadpro97')) return ['32GB', '128GB', '256GB'];

  // iPad Air (M2 2024)
  if (key.includes('ipadair613') || key.includes('ipadair611')) return ['128GB', '256GB', '512GB', '1TB'];
  // iPad Air 5/4/3/2
  if (key.includes('ipadair5')) return ['64GB', '256GB'];
  if (key.includes('ipadair4')) return ['64GB', '256GB'];
  if (key.includes('ipadair3')) return ['64GB', '256GB'];
  if (key.includes('ipadair2')) return ['16GB', '64GB', '128GB'];

  // iPad mini
  if (key.includes('ipadmini7')) return ['128GB', '256GB', '512GB']; // aannemelijke set voor nieuwste
  if (key.includes('ipadmini6')) return ['64GB', '256GB'];
  if (key.includes('ipadmini5')) return ['64GB', '256GB'];
  if (key.includes('ipadmini4')) return ['16GB', '64GB', '128GB'];
  if (key.includes('ipadmini3')) return ['16GB', '64GB', '128GB'];

  // iPad (standaard)
  if (key.includes('ipad10')) return ['64GB', '256GB'];
  if (key.includes('ipad9')) return ['64GB', '256GB'];
  if (key.includes('ipad8')) return ['32GB', '128GB'];
  if (key.includes('ipad7')) return ['32GB', '128GB'];
  if (key.includes('ipad6')) return ['32GB', '128GB'];
  if (key.includes('ipad5')) return ['32GB', '128GB'];

  // Fallback
  return ['64GB', '256GB'];
};

// Map MacBook modellen naar opslagopties
const getMacBookStorageValues = (modelNameRaw: string): string[] => {
  const name = (modelNameRaw || '').toLowerCase();
  const key = name.replace(/\s+|\-/g, '');

  // Silicon generaties (Air / Pro)
  if (key.includes('macbookairsilicon') || (/macbook/.test(key) && /air/.test(key) && /silicon|m1|m2|m3|m4/.test(key))) {
    return ['256GB', '512GB', '1TB', '2TB'];
  }
  if (key.includes('macbookprosilicon') || (/macbook/.test(key) && /pro/.test(key) && /silicon|m1|m2|m3|m4/.test(key))) {
    return ['512GB', '1TB', '2TB', '4TB', '8TB'];
  }

  // Intel generaties
  if (key.includes('macbookprotouch')) {
    return ['256GB', '512GB', '1TB', '2TB', '4TB'];
  }
  if (key.includes('macbookairretina')) {
    return ['128GB', '256GB', '512GB', '1TB'];
  }
  if (key.includes('macbookproretina')) {
    return ['128GB', '256GB', '512GB', '1TB', '2TB'];
  }
  if (key.includes('macbookairlegacy') || (key.includes('macbookair') && !/silicon|retina/.test(key))) {
    return ['128GB', '256GB', '512GB'];
  }

  // Fallback voor overige MacBooks
  if (key.includes('macbook')) {
    return ['256GB', '512GB', '1TB'];
  }

  return ['256GB', '512GB'];
};

// Map Apple Watch modellen naar opslagopties
const getAppleWatchStorageValues = (modelNameRaw: string): string[] => {
  const name = (modelNameRaw || '').toLowerCase();
  const key = name.replace(/\s+|\-/g, '');

  if (key.includes('ultra2')) return ['64GB'];
  if (key.includes('ultra')) return ['32GB'];

  if (key.includes('series10')) return ['64GB'];
  if (key.includes('series9')) return ['64GB'];
  if (key.includes('series8')) return ['32GB'];
  if (key.includes('series7')) return ['32GB'];
  if (key.includes('series6')) return ['32GB'];
  if (key.includes('series5')) return ['32GB'];
  if (key.includes('series4')) return ['16GB'];

  if (key.includes('se2') || key.includes('se (2') || key.includes('se-2')) return ['32GB'];
  if (key.includes('se')) return ['32GB'];

  // Fallback voor onbekende watches
  if (key.includes('applewatch') || key.includes('watch')) return ['32GB'];

  return ['32GB'];
};

// Map iMac modellen naar opslagopties (SSD)
const getIMacStorageValues = (modelNameRaw: string): string[] => {
  const name = (modelNameRaw || '').toLowerCase();
  const key = name.replace(/\s+|\-/g, '');

  if (key.includes('imac24') || key.includes('imacm1') || key.includes('imacm3') || key.includes('imacm4')) {
    return ['256GB', '512GB', '1TB', '2TB'];
  }
  if (key.includes('imacpro')) {
    return ['512GB', '1TB', '2TB', '4TB'];
  }
  if (key.includes('imac27')) {
    return ['256GB', '512GB', '1TB', '2TB', '4TB', '8TB'];
  }
  return ['256GB', '512GB', '1TB'];
};

// Strong, professional container matching the competitor's style
const Container = styled.div`
  background-color: #f8fafc;
  min-height: 100vh;
  padding: 0;
  padding-top: clamp(8rem, 9vw, 9rem);
  padding-bottom: 4rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;

  .dark & {
    background: linear-gradient(to bottom, #0a0c10 0%, #121418 100%);
  }

  @media (max-width: 768px) {
    padding-top: 6.5rem;
    padding-left: 1.5rem;
    padding-right: 1.5rem;
    padding-bottom: 3rem;
  }

  @media (max-width: 640px) {
    padding-top: 6rem;
  }

  @media (max-width: 480px) {
    padding-top: 5.5rem;
    padding-bottom: 2.5rem;
  }
`;

// Modern Progress Bar Component
const ProgressBarContainer = styled.div`
  width: 100%;
  margin: 0 0 1.25rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid #e6edf5;
  display: flex;
  align-items: center;
  gap: 1rem;

  .dark & {
    border-bottom-color: #2e3441;
  }

  @media (max-width: 640px) {
    gap: 0.75rem;
    padding: 0.5rem 0;
    margin: 0 0 0.75rem;
    border-bottom: 1px solid #e6edf5;

    .dark & {
      border-bottom-color: #2e3441;
    }
  }
`;

const ProgressBarWrapper = styled.div`
  flex: 1;
  height: 5px;
  background: #e5e7eb;
  border-radius: 9999px;
  overflow: hidden;

  .dark & {
    background: #1f2937;
  }

  @media (max-width: 640px) {
    height: 8px;
    border-radius: 9999px;
  }
`;

const ProgressBarFill = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'progress',
}) <{ progress: number }>`
  height: 100%;
  background: linear-gradient(90deg, #374151, #111827);
  transition: width 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  width: ${props => props.progress}%;
  border-radius: 9999px;

  .dark & {
    background: linear-gradient(90deg, #94a3b8, #cbd5e1);
  }
`;

const ProgressStepLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  white-space: nowrap;
  flex-shrink: 0;

  .dark & {
    color: #94a3b8;
  }

  @media (max-width: 640px) {
    font-size: 0.8rem;
    font-weight: 700;
    color: #374151;

    .dark & { color: #e5e7eb; }
  }
`;

const ProgressPercentLabel = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  color: #374151;
  white-space: nowrap;
  flex-shrink: 0;
  min-width: 32px;
  text-align: right;

  .dark & {
    color: #e5e7eb;
  }

  @media (max-width: 640px) {
    font-size: 0.8rem;
  }
`;

/* Keep ProgressLabel for backwards compat but hide it */
const ProgressLabel = styled.div`
  display: none;
`;

const ContentArea = styled.div`
  padding: 1.25rem;
  width: 100%;
  max-width: 1100px;
  display: flex;
  flex-direction: column;
  align-items: center;

  @media (max-width: 640px) {
    padding: 0.25rem 0;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  gap: 1.5rem;
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  justify-content: center;

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 1rem;
    max-width: none;
  }
`;

// Clean header without clutter
const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 1.5rem;
  padding: 0;
`;

const PageTitle = styled.h1`
  font-size: 2.5rem; /* Groter and krachtiger */
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.025em; /* Strakkere letter-spacing */
  margin: 0;

  .dark & { color: #e2e8f0; }
`;

const ModelBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background-color: #ffffff;
  border-radius: 9999px; /* Pilvorm */
  padding: 0.5rem 1rem;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);

  .dark & {
    background-color: rgba(17,24,39,0.7);
    border-color: #334155;
    box-shadow: 0 2px 8px rgba(0,0,0,0.45);
  }
`;

const ModelName = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: #374151;

  .dark & { color: #cbd5e1; }
`;

const ModelIcon = styled.div`
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background-color: #f3f4f6;
  display: flex;
  justify-content: center;
  align-items: center;

  img {
    width: 1.5rem;
    height: 1.5rem;
    object-fit: contain;
  }

  .dark & { background-color: #111827; }
`;

// Main layout similar to competitor
const MainContent = styled.div`
  display: flex;
  gap: 1.5rem;
  width: 100%;
  max-width: 1100px; /* center the two main cards visually */
  margin: 0 auto;
  justify-content: center;
  align-items: flex-start; /* prevent squeezing by aligning to top */

  @media (max-width: 1200px) {
    max-width: 1000px;
  }

  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 1rem;
    max-width: none;
  }
`;

// Left column for the question
const QuestionSection = styled.div`
  background: #ffffff;
  padding: 1.5rem;
  border-radius: 24px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
  flex: 1 1 auto;
  height: fit-content;

  .dark & {
    background: #0f1216;
    border: 1px solid #262b36;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  }

  @media (max-width: 640px) {
    padding: 1rem 0.875rem;
    border-radius: 14px;
  }
`;

const QuestionTitle = styled.h2`
  text-align: center;
  font-size: 2rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0 auto 0.5rem auto;
  line-height: 1.2;
  letter-spacing: -0.03em;
  max-width: 820px;

  .dark & { color: #e5e7eb; }
  @media (max-width: 640px) {
    font-size: 1.25rem;
    margin: 0 0 0.2rem 0;
    letter-spacing: -0.02em;
    line-height: 1.2;
  }
`;

const QuestionSubtitle = styled.p`
  text-align: center;
  font-size: 1rem;
  color: #475569;
  margin: 0 auto 1.25rem auto;
  max-width: 820px;
  line-height: 1.55;

  .dark & { color: #94a3b8; }
  @media (max-width: 640px) {
    font-size: 0.8rem;
    margin: 0 auto 0.5rem auto;
    line-height: 1.4;
  }
`;

// OptionsGrid moved to LegacyQuestionStep component

const StorageButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'selected',
}) <{ selected?: boolean }>`
  background: #ffffff;
  color: #111827;
  border: 1px solid #e2e8f0;
  padding: 1.125rem 1rem;
  border-radius: 18px;
  font-size: 1.05rem;
  font-weight: 800;
  letter-spacing: -0.025em;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  isolation: isolate;
  min-height: 100px;
  max-width: 280px;
  width: 100%;
  margin: 0 auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.02);
  display: flex;
  align-items: center;
  justify-content: center;
  transform: translateY(0);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.05);
    border-color: #cbd5e1;
  }

  &:focus-visible {
    outline: 2px solid #0f172a;
    outline-offset: 2px;
  }

  ${props => props.selected && `
    border-color: #0f172a;
    border-width: 2px;
    background: #f8fafc;
    box-shadow: 0 6px 14px rgba(0,0,0,0.05);
    padding: calc(1.125rem - 1px) calc(1rem - 1px);
  `}

  .dark & {
    background: #151921;
    color: #e5e7eb;
    border-color: #262b36;
    box-shadow: 0 4px 12px rgba(0,0,0,0.2);

    &:hover {
      background: #1e293b;
      border-color: #334155;
      box-shadow: 0 8px 24px rgba(0,0,0,0.3);
    }
    
    ${props => props.selected && `
      background: #1e293b;
      border-color: #e2e8f0;
      box-shadow: 0 8px 24px rgba(0,0,0,0.3);
    `}
  }

  @media (max-width: 1024px) {
    padding: 1.25rem 1rem;
    font-size: 1rem;
  }
  @media (max-width: 480px) {
    padding: 0.75rem;
    font-size: 0.95rem;
    border-radius: 14px;
    min-height: 72px;
    max-width: 100%;
  }
`;

// OptionCard, OptionIcon, OptionText, OptionDescription moved to LegacyQuestionStep component

// Right column for device preview
// Right column for device preview
const DevicePreview = styled.div`
  position: sticky;
  top: 5.5rem;
  background: #ffffff;
  border-radius: 24px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  flex: 0 0 340px;
  width: 340px;
  max-width: 340px;
  min-width: 300px;
  height: fit-content;

  @media (max-width: 1024px) {
    position: static;
    max-width: none;
    width: 100%;
    flex-direction: column;
    justify-content: center;
    border-radius: 24px;
    padding: 1.5rem;
  }
  @media (max-width: 640px) {
    display: none;
  }

  .dark & {
    background: #0f1216;
    border: 1px solid #262b36;
    box-shadow: 0 8px 32px rgba(0,0,0,0.5);
  }
`;

// Inline (mobile) preview that visually merges inside the question box
const InlinePreview = styled.div`
  display: none;
  @media (max-width: 640px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border: 1px solid #e2e8f0;
    border-radius: 18px;
    padding: 1.25rem 1rem;
    box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
    box-sizing: border-box;

    .dark & {
      background: linear-gradient(135deg, #1e293b 0%, #151921 100%);
      border: 1px solid #262b36;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
    }

    img {
      max-height: 100px !important;
      width: auto !important;
      height: auto !important;
      object-fit: contain !important;
      margin: 0 auto !important;
      display: block;
      filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.05));
    }

    h3 {
      margin: 0.5rem 0 0 !important;
      font-size: 1.05rem !important;
      font-weight: 700 !important;
      color: #0f172a;

      .dark & { color: #ffffff; }
    }
  }
`;

const InlinePreviewHeading = styled.div`
  width: 100%;
  text-align: center;
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(0, 0, 0, 0.06);

  .dark & {
    border-color: rgba(255, 255, 255, 0.08);
  }

  h2 {
    font-size: 1.1rem;
    margin: 0 0 0.2rem 0;
    text-align: center;
  }

  p {
    font-size: 0.8rem;
    margin: 0;
    text-align: center;
    line-height: 1.4;
    color: #64748b;
  }
`;

const InlineActions = styled.div`
  display: none;
  @media (max-width: 640px) {
    display: block;
    margin-top: 1rem;
    > * + * { margin-top: 0.75rem; }
  }
`;

const ImageContainer = styled.div`
  background: #ffffff;
  border-radius: 24px;
  padding: 1.5rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.04);
  margin-bottom: 1.25rem;
  display: flex;
  justify-content: center;
  align-items: center;

  .dark & {
    background: #1e293b;
    box-shadow: 0 10px 30px rgba(0,0,0,0.3);
  }

  @media (max-width: 640px) {
    padding: 0.5rem;
    border-radius: 14px;
    margin-bottom: 0.25rem;
    box-shadow: none;
    background: transparent;
    border: none;

    .dark & {
      background: transparent;
      box-shadow: none;
    }
  }
`;

// NavigationButtons and NavButton moved to NavigationButtons component

// Modal components (ModalOverlay, ModalCard, ModalTitle, ModalSubtitle, QuickChips, Chip, ModalInput, ModalActions, ModalButton) moved to BatteryHealthModal component

// High quality device image styling is now handled inline for better compatibility

// Enhanced device image component with fallback system
const DeviceImageWithFallback: React.FC<{
  modelName: string;
  alt: string;
  className?: string;
  imageSrc?: string; // Database image URL (takes precedence over generated paths)
}> = ({ modelName, alt, className, imageSrc }) => {
  const [currentImagePath, setCurrentImagePath] = useState<string>('');
  const [imageError, setImageError] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const loadIdRef = useRef<number>(0); // Track stale async operations

  // Get multiple image variants in quality priority order (highest quality first)
  const getImageVariants = (modelName: string): string[] => {
    const name = modelName.toLowerCase().replace(/\s+/g, '-');

    // Device-specific high quality variants
    const baseVariants: string[] = [];

    if (name.includes('iphone')) {
      // iPhone specific variants - prioritize newer high-quality images
      const iphoneModel = name.replace('iphone-', '').replace(/[^a-z0-9-]/g, '-');
      // PascalCase variant (e.g., "17-air" → "Apple-Iphone-17-Air-PNG.png")
      const pascalModel = iphoneModel.split('-').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join('-');
      baseVariants.push(
        `/devices/iphone-${iphoneModel}.png`,           // Current high-quality (32-47KB for newer models)
        `/devices/Apple-Iphone-${pascalModel}-PNG.png`, // PascalCase naming variant
        `/devices/iphone-${iphoneModel}-hd.png`,        // HD variant
        `/devices/iphone-${iphoneModel}-high.png`,      // High quality variant
        `/devices/iphone-${iphoneModel}-medium-png.png`, // Medium quality
      );
      // Generation-based fallback: for new models without images, try previous generation
      const genMatch = iphoneModel.match(/^(\d+)/);
      if (genMatch) {
        const prevGen = parseInt(genMatch[1]) - 1;
        if (prevGen >= 7) {
          baseVariants.push(
            `/devices/iphone-${prevGen}-pro-max.png`,
            `/devices/iphone-${prevGen}-pro.png`,
            `/devices/iphone-${prevGen}.png`,
          );
        }
      }
      baseVariants.push(
        `/devices/iphone-medium-png.png`,               // Better quality generic
        `/devices/iphone.png`                           // Generic fallback
      );
    } else if (name.includes('ipad')) {
      // iPad specific variants - prioritize the high-quality iPad folder images
      const ipadModel = name.replace('ipad-', '').replace(/[^a-z0-9-]/g, '-');

      // Special mapping for iPad Pro models to match the actual file structure
      if (ipadModel.includes('pro-13-7') || ipadModel.includes('pro-13')) {
        baseVariants.push(
          `/iPad/ipad-pro-13inch-7de-gen-medium-png.png`,  // High quality
          `/devices/ipad-pro-13-7.png`                     // Standard fallback
        );
      } else if (ipadModel.includes('pro-12-9-6') || (ipadModel.includes('pro') && ipadModel.includes('12') && ipadModel.includes('6'))) {
        baseVariants.push(
          `/iPad/ipad-pro-12,9inch-6de-gen-medium-png.png`,
          `/devices/ipad-pro-12-9-6.png`
        );
      } else if (ipadModel.includes('pro-12-9-5') || (ipadModel.includes('pro') && ipadModel.includes('12') && ipadModel.includes('5'))) {
        baseVariants.push(
          `/iPad/ipad-pro-12,9inch-5de-gen-medium-png.png`,
          `/devices/ipad-pro-12-9-5.png`
        );
      } else if (ipadModel.includes('pro-12-9-4') || (ipadModel.includes('pro') && ipadModel.includes('12') && ipadModel.includes('4'))) {
        baseVariants.push(
          `/iPad/ipad-pro-12,9inch-4de-gen-medium-png.png`,
          `/devices/ipad-pro-12-9-4.png`
        );
      } else if (ipadModel.includes('pro-12-9-3') || (ipadModel.includes('pro') && ipadModel.includes('12') && ipadModel.includes('3'))) {
        baseVariants.push(
          `/iPad/ipad-pro-12,9inch-3de-gen-medium-png.png`,
          `/devices/ipad-pro-12-9-3.png`
        );
      } else if (ipadModel.includes('pro-11-5') || (ipadModel.includes('pro') && ipadModel.includes('11') && ipadModel.includes('5'))) {
        baseVariants.push(
          `/iPad/ipad-pro-11inch-5de-gen-medium-png.png`,
          `/devices/ipad-pro-11-5.png`
        );
      } else if (ipadModel.includes('pro-11-4') || (ipadModel.includes('pro') && ipadModel.includes('11') && ipadModel.includes('4'))) {
        baseVariants.push(
          `/iPad/ipad-pro-11inch-4de-gen-medium-png.png`,
          `/devices/ipad-pro-11-4.png`
        );
      } else if (ipadModel.includes('pro-11-3') || (ipadModel.includes('pro') && ipadModel.includes('11') && ipadModel.includes('3'))) {
        baseVariants.push(
          `/iPad/ipad-pro-11inch-3de-gen-medium-png.png`,
          `/devices/ipad-pro-11-3.png`
        );
      } else if (ipadModel.includes('mini-7')) {
        baseVariants.push(
          `/iPad/ipad-mini-7-medium-png.png`,              // Very high quality (51KB)
          `/devices/ipad-mini-7.png`
        );
      } else if (ipadModel.includes('mini-6')) {
        baseVariants.push(
          `/iPad/ipad-mini-6-medium-png.png`,
          `/devices/ipad-mini-6.png`
        );
      } else if (ipadModel.includes('mini-5')) {
        baseVariants.push(
          `/iPad/ipad-mini-5-medium-png.png`,
          `/devices/ipad-mini-5.png`
        );
      } else if (ipadModel.includes('air-6-13') || (ipadModel.includes('air') && ipadModel.includes('6') && ipadModel.includes('13'))) {
        baseVariants.push(
          `/iPad/ipad-air-6-13inch-medium-png.png`,        // High quality (22KB)
          `/devices/ipad-air-6-13.png`
        );
      } else if (ipadModel.includes('air-6-11') || (ipadModel.includes('air') && ipadModel.includes('6') && ipadModel.includes('11'))) {
        baseVariants.push(
          `/iPad/ipad-air-6-11inch-medium-png.png`,        // High quality (20KB)
          `/devices/ipad-air-6-11.png`
        );
      } else if (ipadModel.includes('air-5')) {
        baseVariants.push(
          `/iPad/ipad-air-5-medium-png.png`,
          `/devices/ipad-air-5.png`
        );
      } else if (ipadModel.includes('10')) {
        baseVariants.push(
          `/iPad/ipad-10-medium-png.png`,
          `/devices/ipad-10.png`
        );
      } else if (ipadModel.includes('9')) {
        baseVariants.push(
          `/iPad/ipad-9-medium-png.png`,
          `/devices/ipad-9.png`
        );
      } else if (ipadModel.includes('8')) {
        baseVariants.push(
          `/iPad/ipad-8-medium-png.png`,
          `/devices/ipad-8.png`
        );
      }

      // Generic iPad variants
      baseVariants.push(
        `/devices/ipad-${ipadModel}.png`,
        `/iPad/ipad-${ipadModel}-medium-png.png`,
        `/devices/ipad.png`
      );
    } else if (name.includes('samsung') || name.includes('galaxy')) {
      // Samsung specific variants
      const samsungModel = name.replace(/samsung-?|galaxy-?/g, '').replace(/[^a-z0-9-]/g, '-');
      baseVariants.push(
        `/Galaxy S serie/samsung-galaxy-${samsungModel}-medium-png.png`,
        `/devices/samsung-galaxy-${samsungModel}.png`,
        `/devices/samsung.png`
      );
    } else if (name.includes('macbook')) {
      // MacBook specific variants - prioritize silicon models (higher quality)
      const macModel = name.replace('macbook-', '').replace(/[^a-z0-9-]/g, '-');
      if (macModel.includes('silicon')) {
        baseVariants.push(
          `/devices/macbook-${macModel}.png`,              // Silicon models are high quality (25-66KB)
          `/MacBook/macbook-${macModel}-medium-png.png`
        );
      } else {
        baseVariants.push(
          `/devices/macbook-${macModel}.png`,
          `/MacBook/macbook-${macModel}-medium-png.png`
        );
      }
      baseVariants.push(`/devices/macbook.png`);
    } else if (name.includes('imac')) {
      // iMac specific variants
      baseVariants.push(
        `/devices/imac-pro.png`,
        `/iMac/imac-medium-png.png`,
        `/devices/imac.png`
      );
    } else if (name.includes('watch')) {
      // Apple Watch variants
      baseVariants.push(
        `/Apple Watch/serie-10-medium-png.png`,
        `/Apple Watch/se2-medium-png.png`,
        `/devices/apple-watch-medium-png.png`,
        `/devices/apple-watch.png`
      );
    }

    // Fallback to generic device type
    const deviceType = name.split('-')[0];
    baseVariants.push(`/devices/${deviceType}.png`);

    return baseVariants.filter((path, index, self) => self.indexOf(path) === index);
  };

  // Try to load image with fallback system
  const loadImageWithFallback = async (variants: string[]): Promise<string> => {
    for (const variant of variants) {
      try {
        await new Promise<void>((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve();
          img.onerror = () => reject();
          img.src = encodeURI(variant);
        });
        return variant; // Success, return this variant
      } catch {
        continue; // Try next variant
      }
    }
    throw new Error('No valid image found');
  };

  useEffect(() => {
    const thisLoadId = ++loadIdRef.current; // Increment to invalidate previous loads

    // Only show loading spinner on first load (no currentImagePath yet)
    // On subsequent loads (DB image arrived), keep showing existing image
    if (!currentImagePath) {
      setIsLoading(true);
    }
    setImageError(false);

    // Build variant list: database image first (if valid), then generated variants
    const generatedVariants = getImageVariants(modelName);
    const variants = imageSrc && imageSrc !== '/devices/default.png'
      ? [imageSrc, ...generatedVariants]
      : generatedVariants;

    loadImageWithFallback(variants)
      .then((validPath) => {
        // Only apply if this is still the latest load attempt
        if (loadIdRef.current !== thisLoadId) return;
        setCurrentImagePath(validPath);
        setImageError(false);
      })
      .catch(() => {
        if (loadIdRef.current !== thisLoadId) return;
        // Only show error if we don't have any image at all
        if (!currentImagePath) {
          setImageError(true);
        }
      })
      .finally(() => {
        if (loadIdRef.current !== thisLoadId) return;
        setIsLoading(false);
      });
  }, [modelName, imageSrc]);

  if (imageError) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f1f5f9',
        borderRadius: '12px',
        color: '#94a3b8'
      }} className={className}>
        <Smartphone size={24} />
      </div>
    );
  }

  if (isLoading) {
    return <div style={{ width: '100%', height: '100%' }} className={className} />;
  }

  return (
    <img
      src={encodeURI(currentImagePath)}
      alt={alt}
      className={className}
      onError={() => setImageError(true)}
      style={{
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        display: 'block',
        margin: '0 auto'
      }}
    />
  );
};

const DeviceName = styled.h3`
  font-size: 1.5rem;
  font-weight: 800;
  color: #0f172a;
  margin: 0.5rem 0 0.5rem;
  text-align: center;
  line-height: 1.2;
  letter-spacing: -0.025em;

  .dark & { color: #ffffff; }

  @media (max-width: 640px) {
    font-size: 1rem;
    margin: 0.25rem 0 0;
  }

  .model-meta {
    display: block;
    font-size: 0.75em;
    line-height: 1.4;
    margin-top: 0.25rem;
    color: #64748b;
    font-weight: 600;
    letter-spacing: 0.01em;
  }

  .dark & .model-meta { color: #94a3b8; }
`;

const DeviceSpecsContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 0;
`;

const DeviceIdentityCard = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 1.25rem 1rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;

  .dark & {
    background: linear-gradient(135deg, #1e293b 0%, #151921 100%);
    border-color: #262b36;
  }
`;

const DeviceSpecsHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0;
  border-bottom: none;
`;

const SpecBadge = styled.div`
  display: none;
`;

const HeroImage = styled.div`
  width: 90px;
  height: 90px;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.05));

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const HeroTitle = styled.div`
  font-size: 1.1rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.01em;
  text-align: center;

  .dark & { color: white; }

  .model-meta {
    display: block;
    margin-top: 0.15rem;
    font-size: 0.8em;
    font-weight: 600;
    color: #64748b;
    letter-spacing: 0.01em;
  }

  .dark & .model-meta { color: #94a3b8; }
`;



const SpecificationExplainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin: 0.75rem 0 0.5rem;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
  }

  &::before {
    background: linear-gradient(to right, transparent, #cbd5e1);
  }
  &::after {
    background: linear-gradient(to left, transparent, #cbd5e1);
  }

  span {
    padding: 0 0.875rem;
    font-size: 0.62rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: #94a3b8;
    white-space: nowrap;
  }

  .dark & {
    &::before { background: linear-gradient(to right, transparent, #334155); }
    &::after { background: linear-gradient(to left, transparent, #334155); }
    span { color: #64748b; }
  }
`;

const DeviceSpecsTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`;

const DeviceSpecsIcon = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: white;
  color: #0f172a;
  border: 1px solid #e2e8f0;
  box-shadow: 0 2px 4px rgba(0,0,0,0.03);

  svg {
    width: 20px;
    height: 20px;
    stroke-width: 2;
  }
  
  .dark & {
    background: #1e293b;
    border-color: #334155;
    color: #e2e8f0;
  }
`;

const DeviceSpecsTitle = styled.div`
  font-size: 0.8rem;
  font-weight: 700;
  color: #0f172a;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0;
`;

const SpecsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 6px;
  /* Zonder deze breedte kan een lange chip breder worden dan de kaart.
     De kaart centreert zijn inhoud, waardoor die dan links en rechts uitsteekt. */
  width: 100%;
  min-width: 0;
`;

const SpecItem = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'clickable' && prop !== 'blurred' && prop !== 'active',
}) <{ clickable?: boolean; blurred?: boolean; active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0;
  padding: 5px 11px;
  max-width: 100%;
  min-width: 0;
  background: #f1f5f9;
  border-radius: 9999px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1.5px solid transparent;

  ${props => props.active && `
    background: #0f172a;
    border-color: #0f172a;
    & > div > div:first-child { color: rgba(255,255,255,0.5); }
    & > div > div:last-child { color: #ffffff; }
  `}

  ${props => props.blurred && `
    opacity: 0.3;
    filter: grayscale(1);
    pointer-events: none;
  `}

  ${props => props.clickable && !props.blurred && !props.active && `
    cursor: pointer;
    &:hover {
      background: #e2e8f0;
    }
  `}

  .dark & {
    background: #1e293b;

    ${props => props.active && `
      background: #e2e8f0;
      border-color: #e2e8f0;
      & > div > div:first-child { color: rgba(15,23,42,0.5); }
      & > div > div:last-child { color: #0f172a; }
    `}

    ${props => props.clickable && !props.blurred && !props.active && `
      &:hover { background: #334155; }
    `}
  }
`;

const SpecIconWrapper = styled.div<{ $active?: boolean }>`
  display: none;
`;

const SpecContent = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
`;

const SpecLabel = styled.div`
  font-size: 0.6rem;
  color: #64748b;
  font-weight: 500;
  line-height: 1;
  /* Het label benoemt de specificatie en mag nooit half wegvallen; een lang
     antwoord krijgt in plaats daarvan puntjes (zie SpecValue). */
  flex: 0 0 auto;
  white-space: nowrap;

  .dark & { color: #94a3b8; }

  &::after {
    content: ':';
  }
`;

const SpecValue = styled.div`
  font-size: 0.78rem;
  font-weight: 700;
  color: #0f172a;
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  letter-spacing: -0.01em;

  .dark & { color: #f1f5f9; }
`;
/* Removed SpecCheckIcon styled component */

// Question container for different steps
const QuestionContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'visible',
}) <{ visible: boolean }>`
display: ${props => props.visible ? 'block' : 'none'};
`;

// Desktop two-column layout for questions that benefit from illustration
const QuestionTwoCol = styled.div`
  display: grid;
  grid-template-columns: 160px 1px 1fr;
  gap: 24px;
  align-items: start;
  min-width: 100%;
  width: 100%;

  /* Ensure the right column (options) has consistent width */
  > div:last-child {
    min-width: 600px;
    width: 100%;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 150px 1px 1fr;
    > div:last-child {
      min-width: 500px;
    }
  }
  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    > div:last-child {
      min-width: 100%;
    }
  }
`;

// Row that aligns content with the start of the buttons (second column)
const HelperRow = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 24px;
  align-items: start;
  margin-top: 0;
  @media (max-width: 1024px) {
    grid-template-columns: 150px 1fr;
  }
  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
  @media (max-width: 640px) {
    display: flex;
    > div:first-child:empty {
      display: none;
    }
  }
`;

// Heading row so title/subtitle ignore the left illustration column
const HeadingRow = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr;
  gap: 24px;
  align-items: end;
  margin: 0 0 0.5rem 0;
  @media (max-width: 1024px) {
    grid-template-columns: 150px 1fr;
  }
  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
  @media (max-width: 640px) {
    &.desktop-heading {
      display: none;
    }
  }
`;

const IllustrationPanel = styled.div`
  display: none;
  @media (min-width: 861px) {
    display: block;
  }
  /* Keep the panel flush left without inner spacing */
  margin: 0;
`;

const ColumnDivider = styled.div`
  display: none;
  @media (min-width: 861px) {
    display: block;
    align-self: stretch;
    justify-self: center;
    width: 1px;
    background: linear-gradient(180deg, rgba(15,23,42,0.10) 0%, rgba(15,23,42,0.04) 100%);
    border-radius: 1px;
    transform: translateX(-16px);
    @media (min-width: 1280px) {
      transform: translateX(-22px);
    }
  }
`;

const IllustrationCard = styled.div`
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 0;
  height: 380px;
  position: relative;
  overflow: hidden;
  display: block;
  margin-left: -1.25rem;
  box-shadow: none;
`;

const PhoneIllustrationFrame = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-left: 0;

  img, .device-image {
    height: 100%;
    width: auto;
    max-width: none;
    object-fit: contain;
    display: block;
    filter: drop-shadow(0 12px 20px rgba(0, 0, 0, 0.12));
    transform: translateX(-40px);
    will-change: transform, opacity;
    animation: ${phoneSlideIn} 1200ms cubic-bezier(0.16, 1, 0.3, 1) 100ms both;
  }

  @media (min-width: 1280px) {
    img, .device-image {
      height: 100%;
      transform: translateX(-64px);
      animation: ${phoneSlideInWide} 1400ms cubic-bezier(0.16, 1, 0.3, 1) 120ms both;
    }
  }
`;

const IllustrationOverlay = styled.div`
  display: none;
`;

// Enhanced helper text
const HelperText = styled.div`
  margin-top: 0.1rem;
  padding: 1rem 1.25rem;
  background: linear-gradient(180deg, #f8fafc 0%, #eef3f9 100%);
  border-radius: 14px;
  border: 1px solid #e6eaf0;
  box-shadow: 0 3px 12px rgba(15, 23, 42, 0.05) inset, 0 1px 0 rgba(255, 255, 255, 0.6);
  text-align: center;

  @media (max-width: 480px) {
    margin-top: 0.6rem;
    padding: 0.5rem 0.6rem;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(15, 23, 42, 0.035) inset, 0 1px 0 rgba(255, 255, 255, 0.45);
  }

  h4 {
    font-size: 0.95rem;
    font-weight: 800;
    color: #0f172a;
    margin: 0 0 0.5rem 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.65rem;

    svg {
      width: 16px;
      height: 16px;
      color: #1d4ed8;
      padding: 5px;
      border-radius: 9999px;
      background: linear-gradient(180deg, #eef5ff 0%, #e6f0ff 100%);
      border: 1px solid #d7e4ff;
      box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.8);
    }
  }

  @media (max-width: 480px) {
    h4 {
      font-size: 0.82rem;
      margin: 0 0 0.28rem 0;
    }
    h4 svg {
      width: 12px;
      height: 12px;
      padding: 3px;
    }
  }
  @media (max-width: 640px) {
    h4 {
      justify-content: flex-start;
      width: 100%;
      text-align: left;
      margin-left: 0;
      margin-right: 0;
    }
  }

  p {
    font-size: 0.9375rem;
    color: #475569;
    margin: 0 auto;
    line-height: 1.65;
    max-width: 92%;
  }

  /* On mobile, keep everything left-start and indent to align with title text */
  @media (max-width: 640px) {
    p { margin: 0; padding-left: 22px; text-align: left; }
    .helper-steps { padding-left: 22px; }
  }

  @media (max-width: 480px) {
    p {
      font-size: 0.75rem;
      line-height: 1.35;
    }
  }
  @media (max-width: 360px) {
    h4 { font-size: 0.78rem; }
    p { font-size: 0.72rem; }
  }

  .dark & {
    background: #0f1216;
    border: 1px solid #262b36;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.6) inset, 0 1px 0 rgba(255, 255, 255, 0.04);
  }
  .dark & h4 { color: #e2e8f0; }
  .dark & h4 svg { background: linear-gradient(180deg, rgba(75, 85, 99, 0.22) 0%, rgba(75, 85, 99, 0.12) 100%); border: 1px solid #3a4250; color: #e5e7eb; }
  .dark & p { color: #94a3b8; }
`;

const Steps = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  justify-content: center;
  color: #334155;
  width: 100%;

  .dark & { color: #9aa7b5; }
  @media (max-width: 480px) { gap: 2px; }
  @media (max-width: 360px) { gap: 1px; }
  @media (max-width: 640px) {
    justify-content: flex-start;
    padding-left: 22px;
  }
`;

const StepChip = styled.span`
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 9999px;
  padding: 6px 12px;
  font-size: 0.78rem;
  font-weight: 700;
  color: #0f172a;
  box-shadow: 0 1px 0 rgba(255, 255, 255, 0.9) inset;
  line-height: 1.1;
  letter-spacing: -0.02em;

  .dark & {
    background: rgba(17, 24, 39, 0.7);
    border-color: #334155;
    color: #e2e8f0;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);
  }
  @media (max-width: 480px) {
    padding: 1px 5px;
    font-size: 0.6rem;
  }
  @media (max-width: 360px) {
    padding: 1px 4px;
    font-size: 0.56rem;
  }
`;

/* Arrow separator will be declared after SellButton to avoid forward-ref styled warning */

// Spinner animation for loading state
const spinAnimation = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2.5px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spinAnimation} 0.8s linear infinite;
`;

// Enhanced action button (aligned with cart primary button)
const SellButton = styled.button`
  width: 100%;
  padding: 1.125rem;
  background: #1e293b;
  color: white;
  border: none;
  border-radius: 14px;
  font-size: 1.05rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  margin-top: 0.5rem;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.2);
  text-transform: none;
  letter-spacing: -0.01em;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    background: #334155;
    box-shadow: 0 6px 20px rgba(15, 23, 42, 0.25);
  }
  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(15, 23, 42, 0.15);
  }
  &:disabled {
    background: #e2e8f0;
    color: #94a3b8;
    cursor: not-allowed;
    box-shadow: none;
  }

  &.loading {
    background: #334155;
    cursor: wait;
    opacity: 0.9;

    &:hover {
      transform: none;
      box-shadow: 0 2px 8px rgba(15, 23, 42, 0.2);
    }
  }

  &.attention {
    animation: ${attentionPulse} 0.5s ease-in-out 4 !important;
  }

  .dark & {
    background: #e2e8f0;
    color: #0f172a;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);

    &:hover:not(:disabled) {
      background: #f1f5f9;
      box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
    }
    &:disabled {
      background: #262b36;
      color: #64748b;
    }
  }

  @media (max-width: 480px) {
    padding: 0.875rem;
    font-size: 0.94rem;
    border-radius: 12px;
  }

  &.flicker {
    animation: sellFlicker 0.3s ease-in-out 2;
  }

  @keyframes sellFlicker {
    0% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.6; transform: scale(1.03); box-shadow: 0 0 20px rgba(30, 41, 59, 0.4); }
    100% { opacity: 1; transform: scale(1); }
  }
`;

const ArrowSep = styled.span`
  color: #cbd5e1;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  transition: transform 0.2s ease;
  font-size: 0.9em;
  .dark & { color: #94a3b8; }
  ${SellButton}:hover & { transform: translateX(4px); }
`;

const SellArrow = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  transition: transform 0.2s ease;
  ${SellButton}:hover & { transform: translateX(4px); }
  svg {
    width: 16px;
    height: 16px;
    stroke: currentColor;
    stroke-width: 3;
    stroke-linecap: round;
    stroke-linejoin: round;
    fill: none;
  }
`;

// PriceValue and PriceDisplay moved to PriceDisplay component

// E-waste calculation function
const calculateEWasteSaved = (deviceType: string, modelName: string): number => {
  const name = modelName.toLowerCase();

  // E-waste data in grams based on device type and generation
  const eWasteData: { [key: string]: number } = {
    // iPhone models (grams of e-waste saved)
    // iPhone 17 series
    'iphone-18-pro-max': 880,
    'iphone-18-pro': 850,
    'iphone-17-pro-max': 870,
    'iphone-17-pro': 840,
    'iphone-17-air': 800,
    'iphone-17': 770,
    // iPhone 16 series
    'iphone-16-pro-max': 850,
    'iphone-16-pro': 820,
    'iphone-16-plus': 780,
    'iphone-16': 750,
    'iphone-16e': 720,
    'iphone-15-pro-max': 830,
    'iphone-15-pro': 800,
    'iphone-15-plus': 770,
    'iphone-15': 740,
    'iphone-14-pro-max': 810,
    'iphone-14-pro': 780,
    'iphone-14-plus': 750,
    'iphone-14': 720,
    'iphone-13-pro-max': 790,
    'iphone-13-pro': 760,
    'iphone-13-mini': 650,
    'iphone-13': 700,
    'iphone-12-pro-max': 770,
    'iphone-12-pro': 740,
    'iphone-12-mini': 630,
    'iphone-12': 680,
    'iphone-11-pro-max': 750,
    'iphone-11-pro': 720,
    'iphone-11': 660,
    'iphone-xs-max': 730,
    'iphone-xs': 700,
    'iphone-xr': 680,
    'iphone-x': 690,
    'iphone-8-plus': 650,
    'iphone-8': 620,
    'iphone-7-plus': 630,
    'iphone-7': 600,
    'iphone-se-2022': 580,
    'iphone-se-2020': 570,

    // iPad models
    'ipad-pro-13': 1200,
    'ipad-pro-12-9': 1150,
    'ipad-pro-11': 950,
    'ipad-air': 850,
    'ipad-mini': 650,
    'ipad-10': 750,
    'ipad-9': 720,
    'ipad-8': 700,
    'ipad-7': 680,
    'ipad-6': 660,
    'ipad-5': 640,

    // MacBook models
    'macbook-air-silicon': 2800,
    'macbook-pro-silicon': 3200,
    'macbook-pro-touch': 899,
    'macbook-air-retina': 2600,
    'macbook-pro-retina': 3000,
    'macbook-air': 2400,
    'macbook-pro': 2700,

    // iMac models
    'imac-pro': 4500,
    'imac-24': 3800,
    'imac-21': 3500,

    // Apple Watch models
    'apple-watch-series-10': 220,
    'apple-watch-ultra-2': 370,
    'apple-watch-series-9': 160,
    'apple-watch-se-2': 65,
    'apple-watch-ultra': 300,
    'apple-watch-series-8': 120,
    'apple-watch-series-7': 80,
    'apple-watch-se': 45,
    'apple-watch-series-6': 65,
    'apple-watch-series-5': 55,
    'apple-watch-series-4': 35,

    // Samsung Galaxy models
    'samsung-galaxy-s25-ultra': 890,
    'samsung-galaxy-s25-plus': 850,
    'samsung-galaxy-s25': 800,
    'samsung-galaxy-s24-ultra': 870,
    'samsung-galaxy-s24-plus': 830,
    'samsung-galaxy-s24': 780,
    'samsung-galaxy-s23-ultra': 850,
    'samsung-galaxy-s23-plus': 810,
    'samsung-galaxy-s23': 760,
    'samsung-galaxy-s22-ultra': 830,
    'samsung-galaxy-s22-plus': 790,
    'samsung-galaxy-s22': 740,
    'samsung-galaxy-s21-ultra': 810,
    'samsung-galaxy-s21-plus': 770,
    'samsung-galaxy-s21': 720,
    'samsung-galaxy-s20-ultra': 790,
    'samsung-galaxy-s20-plus': 750,
    'samsung-galaxy-s20': 700,
  };

  // Normalize model name for lookup
  const normalizedName = name
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[()]/g, '');

  // Try exact match first
  if (eWasteData[normalizedName]) {
    return eWasteData[normalizedName];
  }

  // Try partial matches for different device types
  if (deviceType.toLowerCase().includes('iphone')) {
    // Find closest iPhone match
    for (const [key, value] of Object.entries(eWasteData)) {
      if (key.includes('iphone') && normalizedName.includes(key.split('-')[1])) {
        return value;
      }
    }
    return 650; // Default iPhone e-waste
  }

  if (deviceType.toLowerCase().includes('ipad')) {
    if (normalizedName.includes('pro')) return 1100;
    if (normalizedName.includes('air')) return 850;
    if (normalizedName.includes('mini')) return 650;
    return 750; // Default iPad e-waste
  }

  if (deviceType.toLowerCase().includes('macbook')) {
    if (normalizedName.includes('pro')) return 3000;
    return 2600; // Default MacBook Air e-waste
  }

  if (deviceType.toLowerCase().includes('imac')) {
    return normalizedName.includes('pro') ? 4500 : 3500;
  }

  if (deviceType.toLowerCase().includes('watch')) {
    return 260;
  }

  if (deviceType.toLowerCase().includes('samsung') || deviceType.toLowerCase().includes('galaxy')) {
    if (normalizedName.includes('ultra')) return 850;
    if (normalizedName.includes('plus')) return 800;
    return 750; // Default Samsung e-waste
  }

  // Default fallback
  return 500;
};

// Compact environmental badge
const EWasteLabel = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'eWasteAmount',
}) <{ eWasteAmount: number }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
  border: 1px solid #bbf7d0;
  border-radius: 14px;
  margin-top: 12px;
  width: 100%;

  .icon {
    width: 34px;
    height: 34px;
    border-radius: 10px;
    background: #166534;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    svg {
      width: 16px;
      height: 16px;
    }
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 1px;
    flex: 1;
    min-width: 0;

    .title {
      font-size: 0.65rem;
      font-weight: 700;
      color: #166534;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      line-height: 1.2;
    }

    .amount {
      font-size: 0.9rem;
      font-weight: 800;
      color: #15803d;
      line-height: 1.2;

      .number {
        font-weight: 800;
      }
    }

    .description {
      font-size: 0.72rem;
      color: #16a34a;
      line-height: 1.35;
    }
  }

  .dark & {
    background: rgba(22, 101, 52, 0.1);
    border-color: rgba(22, 163, 74, 0.2);

    .icon {
      background: #166534;
    }

    .content .title { color: #4ade80; }
    .content .amount { color: #86efac; }
    .content .description { color: #4ade80; }
  }
`;

const EnvironmentalMessage = styled.div`
display: flex;
align - items: center;
gap: 0.75rem;
padding: 0;
background: transparent;
margin - top: 1rem;
  
  svg {
  width: 1rem;
  height: 1rem;
  color: #10b981;
  flex - shrink: 0;
}
  span {
  font - size: 0.75rem;
  color: #64748b;
  font - weight: 400;
  line - height: 1.3;
}
`;

// Navigation buttons removed - moved to top level

// Progress steps removed - only progress bar remains

// Question enum, DynamicQuestion type, and DeviceSpecificationsProps interface imported from ./types

// NOTE: getDeviceImagePath removed - now using database image_path (Single Source of Truth)
// Images are fetched from device_models.image_path via getPublicDeviceModelByKey()
// Default fallback: '/devices/default.png'

const DeviceSpecifications: React.FC<DeviceSpecificationsProps> = ({
  deviceType,
  modelName,
  modelKey,
  modelDisplayName,
  onSellClick
}) => {
  const { language } = useLanguage();
  // Index-based navigation: -1 = Storage selection, 0+ = questions array index
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState<number>(-1);
  // Track if user is editing a single question (clicked from specs list)
  // null = normal flow, number = index of the question being edited (should jump back after answering)
  const [editModeFromIndex, setEditModeFromIndex] = useState<number | null>(null);
  // Track the EXACT position where user was before entering edit mode (to return there)
  const [editModeReturnIndex, setEditModeReturnIndex] = useState<number>(-1);

  // Prioritize modelKey if available, otherwise fall back to modelName (though modelName might be display name)
  // In App.tsx we now pass modelKey={selectedModel} where selectedModel is the key.
  const effectiveModelKey = modelKey || modelName;

  // Legacy flow: Question enum-based state (only used when questions.length === 0)
  const [currentQuestion, setCurrentQuestion] = useState<Question>(Question.Storage);
  const [selectedStorage, setSelectedStorage] = useState<string>('');
  const [availableStorage, setAvailableStorage] = useState<string[]>([]); // Storage options from DB
  const [selectedNetworkType, setSelectedNetworkType] = useState<string>('');
  const [selectedBattery, setSelectedBattery] = useState<string>('');
  const [selectedCondition, setSelectedCondition] = useState<string>('');
  // iPhone specific states
  const [selectedFunctionality, setSelectedFunctionality] = useState<string>('');
  const [selectedScreenCondition, setSelectedScreenCondition] = useState<string>('');
  const [selectedHousingCondition, setSelectedHousingCondition] = useState<string>('');
  const [selectedBatteryCapacity, setSelectedBatteryCapacity] = useState<string>('');
  const [batteryHealthPercent, setBatteryHealthPercent] = useState<string>('');
  const [isBatteryHealthPromptOpen, setBatteryHealthPromptOpen] = useState<boolean>(false);
  const [lastBatteryCapacityChoice, setLastBatteryCapacityChoice] = useState<string>('');
  const batteryInputRef = useRef<HTMLInputElement | null>(null);
  const batteryInputValueRef = useRef<string>('');
  const [hasBatteryModalInteracted, setHasBatteryModalInteracted] = useState(false);
  const [defectsImageError, setDefectsImageError] = useState<boolean>(false);
  // storageImageError is now handled inside StorageStep component
  const [isSelling, setIsSelling] = useState<boolean>(false);
  const sellButtonRef = useRef<HTMLButtonElement | null>(null);

  // Model image from database (Single Source of Truth for device images)
  const [modelImage, setModelImage] = useState<string>('/devices/default.png');

  // Customer-facing model name from device_models.display_name (Single Source of Truth).
  // Seeded from the model list selection; refreshed from the database below.
  const [dbDisplayName, setDbDisplayName] = useState<string | null>(modelDisplayName || null);

  // Get device type key for Supabase queries (normalize to match device_types.key)
  const deviceTypeKey = deviceType.toLowerCase().trim();

  // Dynamic questions from Supabase - using useDeviceQuestions hook
  // Priority: 1) Model-specific sales questions 2) Device-type questions 3) Legacy hardcoded
  const { questions, questionsLoading, questionsError, questionSource } = useDeviceQuestions(
    effectiveModelKey,
    deviceTypeKey,
    language
  );

  // Debug log: Show where questions came from
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && !questionsLoading) {
      console.log('[DeviceSpecifications] Questions loaded:', {
        source: questionSource,
        count: questions.length,
        modelKey: effectiveModelKey,
        deviceTypeKey,
        questions: questions.map(q => q.questionKey),
      });
    }
  }, [questions, questionsLoading, questionSource, effectiveModelKey, deviceTypeKey]);

  // Answers: question_key -> option_key - using useAnswers hook
  const { answers, updateAnswer, preserveAnswers } = useAnswers(questions);

  // Preserve answers when questions change
  useEffect(() => {
    if (questions.length > 0) {
      preserveAnswers(questions);
    }
  }, [questions, preserveAnswers]);

  // In dynamic mode, start at index 0 (skip legacy StorageStep)
  // This effect runs when questions are first loaded
  useEffect(() => {
    if (questions.length > 0 && currentQuestionIndex === -1) {
      // Dynamic mode: skip to first question (index 0)
      setCurrentQuestionIndex(0);
    }
  }, [questions.length]); // Only depend on questions.length to avoid infinite loops

  // Fetch storage options from Price Matrix (Single Source of Truth)
  useEffect(() => {
    let isMounted = true;

    const fetchStorageOptions = async () => {
      if (!effectiveModelKey) return;

      const options = await getPublicStorageOptionsByModelKey(effectiveModelKey);

      if (isMounted) {
        // If options found in DB, use them. Otherwise empty array (StorageStep will use fallback)
        setAvailableStorage(options);
      }
    };

    fetchStorageOptions();

    return () => {
      isMounted = false;
    };
  }, [effectiveModelKey]);

  // Fetch model image from database (Single Source of Truth - replaces hardcoded image paths)
  useEffect(() => {
    let isMounted = true;

    const fetchModelImage = async () => {
      if (!effectiveModelKey) return;

      const modelData = await getPublicDeviceModelByKey(effectiveModelKey, language);

      if (!isMounted || !modelData) return;
      if (modelData.image) setModelImage(modelData.image);
      // getPublicDeviceModelByKey falls back to the key when display_name is empty; keep that out.
      if (modelData.name && modelData.name !== effectiveModelKey) setDbDisplayName(modelData.name);
    };

    fetchModelImage();

    return () => {
      isMounted = false;
    };
  }, [effectiveModelKey, language]);

  // Reset to the selection-time name whenever another model is opened.
  useEffect(() => {
    setDbDisplayName(modelDisplayName || null);
  }, [modelDisplayName, effectiveModelKey]);

  // Derived names: full form for the cart/e-mail, short form for running text,
  // and base + meta ("iPad Pro 12.9-inch (4th generation)" + "2020") for titles.
  const displayName = resolveDeviceDisplayName(dbDisplayName, effectiveModelKey);
  const displayNameShort = resolveDeviceDisplayName(dbDisplayName, effectiveModelKey, { short: true });
  const { base: displayNameBase, meta: displayNameRawMeta } = splitDeviceDisplayName(dbDisplayName || displayName);
  const displayNameMeta = formatDeviceNameMeta(displayNameBase, displayNameRawMeta);

  const t = (key: string) => getTranslation(language as Language, key);

  // Storage options are now handled by StorageStep component

  // Option arrays moved to LegacyQuestionStep component

  // Device type detection
  const isIPhone = deviceType.toLowerCase().includes('iphone');
  const isIPad = deviceType.toLowerCase().includes('ipad');
  const isIMac = deviceType.toLowerCase().includes('imac');

  // Only show network question for iPads
  const shouldShowNetworkQuestion = isIPad;
  // Only show battery question for devices that have batteries (not iMacs)
  const shouldShowBatteryQuestion = !isIMac;

  /**
   * Map dynamic question's questionKey to a Question step
   * This determines which wizard step the question should appear in
   */
  function getQuestionStepForDynamic(question: DynamicQuestion): Question {
    const key = question.questionKey.toLowerCase();

    // explicit overrides first
    if (key === 'test_condition') return Question.Condition;

    // pattern-based mapping
    if (key.startsWith('storage')) return Question.Storage;
    if (key.startsWith('condition') || key.includes('condition')) return Question.Condition;
    if (key.startsWith('screen') || key.includes('screen')) return Question.ScreenCondition;
    if (key.startsWith('housing') || key.includes('housing') || key.includes('body')) return Question.HousingCondition;
    if (key.startsWith('battery') || key.includes('battery')) {
      // For iPhone, use BatteryCapacity; for others, use Battery
      return isIPhone ? Question.BatteryCapacity : Question.Battery;
    }
    if (key.startsWith('functionality') || key.includes('functionality')) return Question.Functionality;
    if (key.startsWith('network') || key.includes('network')) return Question.NetworkType;

    // default fallback: show in Condition step
    return Question.Condition;
  }

  const getNextQuestion = (current: Question): Question | null => {
    if (isIPhone) {
      // iPhone specific question flow
      switch (current) {
        case Question.Storage:
          return Question.Functionality;
        case Question.Functionality:
          return Question.ScreenCondition;
        case Question.ScreenCondition:
          return Question.HousingCondition;
        case Question.HousingCondition:
          return Question.BatteryCapacity;
        case Question.BatteryCapacity:
          return null;
        default:
          return null;
      }
    } else {
      // Original flow for other devices
      switch (current) {
        case Question.Storage:
          return shouldShowNetworkQuestion ? Question.NetworkType :
            shouldShowBatteryQuestion ? Question.Battery : Question.Condition;
        case Question.NetworkType:
          return shouldShowBatteryQuestion ? Question.Battery : Question.Condition;
        case Question.Battery:
          return Question.Condition;
        case Question.Condition:
          return null;
        default:
          return null;
      }
    }
  };

  const getPreviousQuestion = (current: Question): Question | null => {
    if (isIPhone) {
      // iPhone specific question flow
      switch (current) {
        case Question.Functionality:
          return Question.Storage;
        case Question.ScreenCondition:
          return Question.Functionality;
        case Question.HousingCondition:
          return Question.ScreenCondition;
        case Question.BatteryCapacity:
          return Question.HousingCondition;
        default:
          return null;
      }
    } else {
      // Original flow for other devices
      switch (current) {
        case Question.NetworkType:
          return Question.Storage;
        case Question.Battery:
          return shouldShowNetworkQuestion ? Question.NetworkType : Question.Storage;
        case Question.Condition:
          return shouldShowBatteryQuestion ? Question.Battery :
            shouldShowNetworkQuestion ? Question.NetworkType : Question.Storage;
        default:
          return null;
      }
    }
  };

  const handleStorageSelect = (storage: string) => {
    setSelectedStorage(storage);

    // If in edit mode (editing storage specifically), return to saved position
    if (editModeFromIndex === -1) {
      const returnTo = editModeReturnIndex; // Save before clearing
      setEditModeFromIndex(null);
      setEditModeReturnIndex(-1);

      // Return to exactly where we were before editing
      setTimeout(() => {
        setCurrentQuestionIndex(returnTo);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 50);
    } else if (questions.length > 0) {
      // Normal flow: after storage selection, move to first question
      setCurrentQuestionIndex(0);
    }
  };

  const handleNetworkTypeSelect = (networkType: string) => {
    setSelectedNetworkType(networkType);
    // Legacy handler - only used when questions.length === 0
    // For dynamic questions, this is handled by the dynamic question onClick
  };

  const handleBatterySelect = (battery: string) => {
    setSelectedBattery(battery);
    // Legacy handler - only used when questions.length === 0
  };

  const handleConditionSelect = (condition: string) => {
    setSelectedCondition(condition);
    // Legacy handler - only used when questions.length === 0
  };

  // iPhone specific handlers - Legacy only
  const handleFunctionalitySelect = (functionality: string) => {
    setSelectedFunctionality(functionality);
    // Legacy handler - only used when questions.length === 0
  };

  const handleScreenConditionSelect = (screenCondition: string) => {
    setSelectedScreenCondition(screenCondition);
    // Legacy handler - only used when questions.length === 0
  };

  const handleHousingConditionSelect = (housingCondition: string) => {
    setSelectedHousingCondition(housingCondition);
    // Legacy handler - only used when questions.length === 0
  };

  const handleBatteryCapacitySelect = (batteryCapacity: string) => {
    setSelectedBatteryCapacity(batteryCapacity);
    // For iPhone, immediately ask for exact health if known (for above/below 85%)
    if (isIPhone && (batteryCapacity === 'above85' || batteryCapacity === 'below85')) {
      setLastBatteryCapacityChoice(batteryCapacity);
      // Clear input DOM directly when opening modal (will be ready when modal renders)
      setTimeout(() => {
        if (batteryInputRef.current) {
          batteryInputRef.current.value = '';
        }
      }, 0);
      batteryInputValueRef.current = '';
      setHasBatteryModalInteracted(false);
      setBatteryHealthPromptOpen(true);
    }
    // Legacy handler - only used when questions.length === 0
  };

  // iPhone pricing matrix (base price per storage)
  const iPhonePriceMatrix: Record<string, Record<string, number>> = {
    'iphone-8': { '64GB': 40, '256GB': 60 },
    'iphone-se-2020': { '64GB': 50, '128GB': 70, '256GB': 100 },
    'iphone-se-2022': { '64GB': 70, '128GB': 100, '256GB': 120 },
    'iphone-8-plus': { '64GB': 50, '128GB': 70, '256GB': 80 },
    'iphone-x': { '64GB': 60, '256GB': 90 },
    'iphone-xr': { '64GB': 60, '256GB': 90 },
    'iphone-xs': { '64GB': 70, '256GB': 90, '512GB': 110 },
    'iphone-xs-max': { '64GB': 80, '256GB': 100, '512GB': 120 },
    'iphone-11': { '64GB': 100, '128GB': 120, '256GB': 150 },
    'iphone-11-pro': { '64GB': 150, '256GB': 180, '512GB': 200 },
    'iphone-11-pro-max': { '64GB': 170, '256GB': 200, '512GB': 230 },
    'iphone-12-mini': { '64GB': 100, '128GB': 120, '256GB': 150 },
    'iphone-12': { '64GB': 130, '128GB': 150, '256GB': 200 },
    'iphone-12-pro': { '128GB': 200, '256GB': 250, '512GB': 270 },
    'iphone-12-pro-max': { '128GB': 250, '256GB': 300, '512GB': 320 },
    'iphone-13-mini': { '128GB': 150, '256GB': 180, '512GB': 200 },
    'iphone-13': { '128GB': 200, '256GB': 230, '512GB': 250 },
    'iphone-13-pro': { '128GB': 300, '256GB': 330, '512GB': 350, '1TB': 400 },
    'iphone-13-pro-max': { '128GB': 400, '256GB': 430, '512GB': 450, '1TB': 500 },
    'iphone-14': { '128GB': 250, '256GB': 300, '512GB': 330 },
    'iphone-14-plus': { '128GB': 300, '256GB': 350, '512GB': 400 },
    'iphone-14-pro': { '128GB': 400, '256GB': 450, '512GB': 470, '1TB': 500 },
    'iphone-14-pro-max': { '128GB': 500, '256GB': 550, '512GB': 580, '1TB': 600 },
    'iphone-15': { '128GB': 400, '256GB': 430, '512GB': 450 },
    'iphone-15-plus': { '128GB': 420, '256GB': 450, '512GB': 470 },
    'iphone-15-pro': { '128GB': 550, '256GB': 600, '512GB': 650, '1TB': 700 },
    'iphone-15-pro-max': { '256GB': 700, '512GB': 750, '1TB': 800 },
    'iphone-16e': { '128GB': 350, '256GB': 370, '512GB': 380 },
    'iphone-16': { '128GB': 500, '256GB': 550, '512GB': 580 },
    'iphone-16-plus': { '128GB': 650, '256GB': 680, '512GB': 700 },
    'iphone-16-pro': { '128GB': 750, '256GB': 800, '512GB': 830, '1TB': 850 },
    'iphone-16-pro-max': { '128GB': 800, '256GB': 850, '512GB': 900, '1TB': 950 },
    // iPhone 17 series
    'iphone-17': { '128GB': 600, '256GB': 650, '512GB': 680, '1TB': 700 },
    'iphone-17-pro': { '128GB': 950, '256GB': 1000, '512GB': 1050, '1TB': 1100 },
    'iphone-17-pro-max': { '128GB': 1050, '256GB': 1100, '512GB': 1150, '1TB': 1200 },
    'iphone-17-air': { '128GB': 800, '256GB': 850, '512GB': 900, '1TB': 950 },
  };

  const getIphoneBasePrice = (modelId: string, storage: string): number | null => {
    const slug = (modelId || '').toLowerCase().replace(/\s+/g, '-');
    const table = iPhonePriceMatrix[slug];
    if (!table) return null;
    const price = (table as Record<string, number>)[storage];
    return typeof price === 'number' ? price : null;
  };

  // iPad base price matrix (baseline for 64/128/256GB before storage/network adjustments)
  const iPadBasePriceMatrix: Record<string, number> = {
    // iPad Pro 13"
    'ipad-pro-13-7': 1230,
    // iPad Pro 12.9"
    'ipad-pro-12-9-6': 740,
    'ipad-pro-12-9-5': 620,
    'ipad-pro-12-9-4': 520,
    'ipad-pro-12-9-3': 365,
    'ipad-pro-12-9-2': 260,
    'ipad-pro-12-9-1': 160,
    // iPad Pro 11"
    'ipad-pro-11-5': 1150,
    'ipad-pro-11-4': 620,
    'ipad-pro-11-3': 515,
    'ipad-pro-11-2': 365,
    'ipad-pro-11-1': 295,
    // Older Pro sizes
    'ipad-pro-10-5': 170,
    'ipad-pro-9-7': 95,
    // iPad Air
    'ipad-air-6-13': 770,
    'ipad-air-6-11': 720,
    'ipad-air-5': 300,
    'ipad-air-4': 235,
    'ipad-air-3': 165,
    'ipad-air-2': 25,
    // iPad mini
    'ipad-mini-7': 560,
    'ipad-mini-6': 260,
    'ipad-mini-5': 155,
    'ipad-mini-4': 30,
    'ipad-mini-3': 20,
    // iPad (standard)
    'ipad-10': 230,
    'ipad-9': 190,
    'ipad-8': 120,
    'ipad-7': 100,
  };

  // MacBook base price matrix (cap values)
  const macBookBasePriceMatrix: Record<string, number> = {
    'macbook-air-silicon': 999,
    'macbook-pro-silicon': 1299,
    'macbook-pro-touch': 899,
    'macbook-air-retina': 699,
    'macbook-pro-retina': 799,
    'macbook-air-legacy': 499,
  };

  const getMacBookBaseMaxForStorage = (modelId: string, storage: string): number | null => {
    const slug = (modelId || '').toLowerCase().replace(/\s+/g, '-');
    const base = macBookBasePriceMatrix[slug];
    if (typeof base !== 'number') return null;
    // MacBooks: storage reduces price for lower tiers, baseline is 256GB (Air Silicon) / 512GB (Pro Silicon)
    // We keep a simple rule similar to README: 128 -40%, 256 -30%, 512 -20%, 1TB -15%, 2TB -10%, 4TB -5%, 8TB 0%
    const multiplier: Record<string, number> = {
      '128GB': 0.6,
      '256GB': 0.7,
      '512GB': 0.8,
      '1TB': 0.85,
      '2TB': 0.9,
      '4TB': 0.95,
      '8TB': 1.0,
    };
    // If storage not listed, default to 1.0 cap
    const m = multiplier[storage] ?? 1.0;
    return Math.round(base * m);
  };

  const getIPadBaseMaxForStorage = (modelId: string, storage: string): number | null => {
    const slug = (modelId || '').toLowerCase().replace(/\s+/g, '-');
    const base = iPadBasePriceMatrix[slug];
    if (typeof base !== 'number') return null;
    const storageMultiplier: Record<string, number> = {
      '64GB': 1,
      '128GB': 1,
      '256GB': 1,
      '512GB': 1.18,
      '1TB': 1.36,
    };
    const m = storageMultiplier[storage] ?? 1;
    return Math.round(base * m);
  };

  // Samsung pricing matrix
  const samsungPriceMatrix: Record<string, Record<string, number>> = {
    // S20 serie
    'samsung-galaxy-s20': { '128GB': 50 },
    'samsung-galaxy-s20-plus': { '128GB': 70, '512GB': 70 },
    'samsung-galaxy-s20-ultra': { '128GB': 150, '256GB': 150, '512GB': 150 },

    // S21 serie
    'samsung-galaxy-s21-fe': { '128GB': 50, '256GB': 70 },
    'samsung-galaxy-s21': { '128GB': 70, '256GB': 110 },
    'samsung-galaxy-s21-plus': { '128GB': 120, '256GB': 150 },
    'samsung-galaxy-s21-ultra': { '128GB': 170, '256GB': 200, '512GB': 230 },

    // S22 serie
    'samsung-galaxy-s22': { '128GB': 100, '256GB': 160 },
    'samsung-galaxy-s22-plus': { '128GB': 150, '256GB': 180 },
    'samsung-galaxy-s22-ultra': { '128GB': 200, '256GB': 250, '512GB': 270, '1TB': 300 },

    // S23 serie
    'samsung-galaxy-s23-fe': { '128GB': 100, '256GB': 150 },
    'samsung-galaxy-s23': { '128GB': 150, '256GB': 200, '512GB': 230 },
    'samsung-galaxy-s23-plus': { '128GB': 200, '256GB': 250, '512GB': 300 },
    'samsung-galaxy-s23-ultra': { '256GB': 350, '512GB': 400, '1TB': 435 },

    // S24 serie
    'samsung-galaxy-s24-fe': { '128GB': 150, '256GB': 200 },
    'samsung-galaxy-s24': { '128GB': 200, '256GB': 250, '512GB': 280 },
    'samsung-galaxy-s24-plus': { '256GB': 280, '512GB': 330 },
    'samsung-galaxy-s24-ultra': { '256GB': 450, '512GB': 550, '1TB': 580 },

    // S25 serie
    'samsung-galaxy-s25': { '128GB': 400, '256GB': 440, '512GB': 440 },
    'samsung-galaxy-s25-plus': { '256GB': 470, '512GB': 520 },
    'samsung-galaxy-s25-ultra': { '256GB': 600, '512GB': 650, '1TB': 700 },
  };

  const getSamsungBasePrice = (modelId: string, storage: string): number | null => {
    const slug = (modelId || '').toLowerCase().replace(/\s+/g, '-');
    const table = samsungPriceMatrix[slug];
    if (!table) return null;
    const price = (table as Record<string, number>)[storage];
    return typeof price === 'number' ? price : null;
  };

  // ── Question phase: single source of truth for which UI to show ──
  // Eliminates scattered conditionals and prevents flash of wrong content.
  const questionPhase: 'loading' | 'dynamic' | 'legacy' | 'error' =
    questionsLoading         ? 'loading' :
    questionsError           ? 'error'   :
    questions.length > 0     ? 'dynamic' :
    /* no questions, done */   'legacy';

  // Dynamic flow helpers
  const hasDynamicQuestions = questionPhase === 'dynamic';

  // Check if dynamic questions include a storage question
  // If yes, we skip the legacy StorageStep and let the dynamic question handle it
  const storageQuestionKey = useMemo(() => {
    if (!hasDynamicQuestions) return null;
    const storageQ = questions.find(q =>
      q.questionKey.toLowerCase().includes('storage') ||
      q.questionKey.toLowerCase().includes('opslag')
    );
    return storageQ?.questionKey || null;
  }, [hasDynamicQuestions, questions]);

  // In dynamic mode, skip the StorageStep entirely - all questions come from DB
  const skipStorageStep = hasDynamicQuestions;

  // Get effective storage value: from dynamic answers (if storage question exists) or legacy selectedStorage
  const effectiveStorage = useMemo(() => {
    if (storageQuestionKey && answers[storageQuestionKey]) {
      return answers[storageQuestionKey];
    }
    return selectedStorage;
  }, [storageQuestionKey, answers, selectedStorage]);

  // Memoize legacyState to prevent unnecessary recalculations
  const legacyState = useMemo(() => ({
    selectedCondition,
    selectedFunctionality,
    selectedScreenCondition,
    selectedHousingCondition,
    selectedBatteryCapacity,
    selectedNetworkType,
    selectedBattery,
  }), [
    selectedCondition,
    selectedFunctionality,
    selectedScreenCondition,
    selectedHousingCondition,
    selectedBatteryCapacity,
    selectedNetworkType,
    selectedBattery,
  ]);

  // Price calculation - using usePriceCalculation hook
  // Use effectiveStorage which can come from dynamic answers or legacy selectedStorage
  const { price, isPricing, priceError, isOnRequest, calculatePrice } = usePriceCalculation({
    modelName,
    selectedStorage: effectiveStorage,
    answers,
    hasDynamicQuestions,
    isIPhone,
    legacyState,
    auto: true,
  });

  // Price calculation is now handled by usePriceCalculation hook

  // Prevent body scroll when battery health modal is open
  useEffect(() => {
    if (isBatteryHealthPromptOpen) {
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [isBatteryHealthPromptOpen]);

  // Battery health input handlers are now in BatteryHealthModal component

  const handleSaveBatteryHealth = () => {
    // Get value from ref (most up-to-date, no state dependency)
    const inputValue = batteryInputValueRef.current || batteryInputRef.current?.value || '';

    // Normalize input like "88" or "88%"
    const raw = String(inputValue).replace(/[^0-9]/g, '').slice(0, 3);
    let num = parseInt(raw || '');
    if (!isNaN(num) && raw.length > 0) {
      // constrain to 0..100
      num = Math.max(0, Math.min(100, num));
      const display = num.toString();
      // Business rule: if user selected below 85 and entered >=85, cap at 84
      if (lastBatteryCapacityChoice === 'below85' && num >= 85) {
        setBatteryHealthPercent('84');
      } else if (lastBatteryCapacityChoice === 'above85' && num < 85) {
        // If selected above 85 but entered <85, bump to 85
        setBatteryHealthPercent('85');
      } else {
        setBatteryHealthPercent(display);
      }
    } else {
      setBatteryHealthPercent('');
    }
    setBatteryHealthPromptOpen(false);
    setHasBatteryModalInteracted(false);
  };

  const handleSkipBatteryHealth = () => {
    // Clear DOM directly (uncontrolled)
    if (batteryInputRef.current) {
      batteryInputRef.current.value = '';
    }
    batteryInputValueRef.current = '';
    setBatteryHealthPercent('');
    setBatteryHealthPromptOpen(false);
    setHasBatteryModalInteracted(false);
  };

  // Index-based navigation handlers
  const handleNext = () => {
    if (currentQuestionIndex === -1 && selectedStorage && questions.length > 0) {
      // Move from storage to first question
      setCurrentQuestionIndex(0);
    } else if (currentQuestionIndex >= 0 && currentQuestionIndex < questions.length - 1) {
      // Move to next question
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      // Move to previous question
      setCurrentQuestionIndex(prev => prev - 1);
    } else if (currentQuestionIndex === 0 && !skipStorageStep) {
      // Legacy mode only: Go back to storage selection
      setCurrentQuestionIndex(-1);
    }
    // In dynamic mode (skipStorageStep), at index 0 we don't go back further
  };

  const currentDynamicQuestion =
    hasDynamicQuestions && currentQuestionIndex >= 0 && currentQuestionIndex < questions.length
      ? questions[currentQuestionIndex]
      : null;

  // Check if we can proceed to next question (unified for both legacy and dynamic flows)
  const canProceed = () => {
    // Legacy flow: no dynamic questions from Supabase
    if (questions.length === 0) {
      switch (currentQuestion) {
        case Question.Storage:
          return !!selectedStorage;
        case Question.NetworkType:
          return !!selectedNetworkType;
        case Question.Battery:
          return !!selectedBattery;
        case Question.Condition:
          return !!selectedCondition;
        // iPhone specific legacy questions
        case Question.Functionality:
          return !!selectedFunctionality;
        case Question.ScreenCondition:
          return !!selectedScreenCondition;
        case Question.HousingCondition:
          return !!selectedHousingCondition;
        case Question.BatteryCapacity:
          return !!selectedBatteryCapacity;
        default:
          return false;
      }
    }

    // Dynamic flow: Supabase-driven questions + index-based navigation
    // In dynamic mode, we skip the storage step (index -1) entirely
    if (currentDynamicQuestion) {
      // Check if current question is answered
      const answer = answers[currentDynamicQuestion.questionKey];
      return currentDynamicQuestion.isRequired ? !!answer : true;
    }
    return false;
  };

  const handleSellDevice = () => {
    // Prevent multiple clicks
    if (isSelling) return;

    // Early return if not all questions are answered
    if (!areAllQuestionsAnswered()) return;

    // CRITICAL: Prevent adding to cart while price is calculating
    if (isPricing) {
      console.warn('[handleSellDevice] Blocked: Price calculation still in progress');
      return;
    }

    // Allow On Request items to proceed even without a fixed price
    // Otherwise, price must be a valid positive number
    if (!isOnRequest && (typeof price !== 'number' || price < 0)) {
      console.warn('[handleSellDevice] Blocked: Invalid price and not On Request', { price, isPricing, priceError, isOnRequest });
      return;
    }

    // Build specs payload
    const specs = buildSellSpecsLocal();
    if (!specs) return;

    // Debug log for offer request payload (development only)
    if (process.env.NODE_ENV === 'development') {
      const offerPayload = buildOfferRequestPayload(
        modelName,
        selectedStorage,
        answers,
        hasDynamicQuestions,
        isIPhone,
        {
          selectedCondition,
          selectedFunctionality,
          selectedScreenCondition,
          selectedHousingCondition,
          selectedBatteryCapacity,
          selectedNetworkType,
          selectedBattery,
        }
      );
      console.log('[OFFER REQUEST PAYLOAD]', offerPayload);
    }

    // Set loading state
    setIsSelling(true);

    // Navigate immediately
    onSellClick(specs);
  };


  // Unified helper to check if all questions are answered (both legacy and dynamic flows)
  const areAllQuestionsAnswered = (): boolean => {
    // 1) Legacy flow: no dynamic questions from Supabase
    if (!hasDynamicQuestions) {
      // Reuse existing legacy logic
      if (isIPhone) {
        return !!(
          selectedStorage &&
          selectedFunctionality &&
          selectedScreenCondition &&
          selectedHousingCondition &&
          selectedBatteryCapacity
        );
      } else {
        return !!(
          selectedStorage &&
          selectedCondition &&
          (!shouldShowNetworkQuestion || selectedNetworkType) &&
          (!shouldShowBatteryQuestion || selectedBattery)
        );
      }
    }

    // 2) Dynamic mode: Only Supabase questions (storage is one of them if needed)
    // No separate storage step - all questions come from database
    // Check if all required questions are answered
    // Use answers state which stores questionKey -> optionKey mappings
    return questions.every((q) => {
      // If question is not required, consider it answered
      if (!q.isRequired) return true;

      // Check if answer exists in answers state
      const answer = answers[q.questionKey];
      return !!answer;
    });
  };

  // Keep isComplete for backward compatibility (now uses unified helper)
  const isComplete = areAllQuestionsAnswered();

  // Check if price is valid for selling (must be a positive number, not null/calculating)
  const hasValidPrice = typeof price === 'number' && price >= 0;

  // Combined check: can we enable the sell button?
  // Must have: all questions answered, (valid price OR on request), not currently selling or calculating
  // On Request items are allowed to proceed even without a fixed price
  const canSell = isComplete && (hasValidPrice || isOnRequest) && !isSelling && !isPricing;

  // DEBUG: Log button state on every render
  console.log('[DeviceSpecifications] BUTTON STATE DEBUG:', {
    isComplete,
    hasValidPrice,
    isOnRequest,
    isSelling,
    isPricing,
    price,
    priceError,
    canSell,
    // Breakdown of canSell calculation
    canSellBreakdown: {
      step1_isComplete: isComplete,
      step2_priceOrRequest: hasValidPrice || isOnRequest,
      step3_notSelling: !isSelling,
      step4_notPricing: !isPricing,
      final: isComplete && (hasValidPrice || isOnRequest) && !isSelling && !isPricing,
    }
  });

  // Build specs payload for onSellClick (navigation/cart) - using buildSellSpecs utility
  const buildSellSpecsLocal = () => {
    return buildSellSpecs(
      effectiveStorage, // Use effectiveStorage which can come from dynamic answers or legacy
      answers,
      hasDynamicQuestions,
      isIPhone,
      price,
      modelName,
      () => modelImage, // Use database image (Single Source of Truth)
      batteryHealthPercent,
      {
        selectedNetworkType,
        selectedBattery,
        selectedCondition,
        selectedFunctionality,
        selectedScreenCondition,
        selectedHousingCondition,
        selectedBatteryCapacity,
      },
      questions, // Pass questions array to build dynamicSpecs with labels
      isOnRequest, // Pass isOnRequest to allow On Request items
      displayName // Customer-facing name for cart, checkout and e-mail
    );
  };

  // Debug log for all questions state (development only)
  if (process.env.NODE_ENV === 'development') {
    console.log('[ALL QUESTIONS STATE]', {
      hasDynamicQuestions,
      selectedStorage,
      questions: questions.map((q) => ({
        key: q.questionKey,
        isRequired: q.isRequired,
        isAnswered: !!answers[q.questionKey],
      })),
      allQuestionsAnswered: areAllQuestionsAnswered(),
      legacyChecks: !hasDynamicQuestions ? {
        isIPhone,
        selectedFunctionality,
        selectedScreenCondition,
        selectedHousingCondition,
        selectedBatteryCapacity,
        selectedCondition,
        selectedNetworkType,
        selectedBattery,
      } : null,
    });
  }


  // Calculate progress based on completed questions
  const getProgressSteps = () => {
    // Dynamic questions mode - use questions array from database
    // No separate storage step — storage is part of dynamic questions if needed
    if (hasDynamicQuestions && questions.length > 0) {
      return questions.map((q) => ({
        key: q.questionKey,
        label: q.label || q.questionKey,
        completed: !!answers[q.questionKey],
        required: q.isRequired,
      }));
    }

    // Legacy iPhone flow
    if (isIPhone) {
      return [
        { key: 'storage', label: t('storage'), completed: !!selectedStorage, required: true },
        { key: 'functionality', label: t('functionality'), completed: !!selectedFunctionality, required: true },
        { key: 'screen', label: t('screen'), completed: !!selectedScreenCondition, required: true },
        { key: 'housing', label: t('housing'), completed: !!selectedHousingCondition, required: true },
        { key: 'battery', label: t('battery'), completed: !!selectedBatteryCapacity, required: true },
      ];
    } else {
      const steps = [
        { key: 'storage', label: t('storage'), completed: !!selectedStorage, required: true },
      ];

      if (shouldShowNetworkQuestion) {
        steps.push({ key: 'network', label: t('network'), completed: !!selectedNetworkType, required: true });
      }

      if (shouldShowBatteryQuestion) {
        steps.push({ key: 'battery', label: t('battery'), completed: !!selectedBattery, required: true });
      }

      steps.push({ key: 'condition', label: t('condition'), completed: !!selectedCondition, required: true });

      return steps;
    }
  };

  const getProgressPercentage = () => {
    const steps = getProgressSteps();
    const completedSteps = steps.filter(step => step.completed).length;
    return steps.length > 0 ? Math.round((completedSteps / steps.length) * 100) : 0;
  };

  const getCompletedCount = () => {
    return getProgressSteps().filter(step => step.completed).length;
  };

  const getCurrentStepIndex = () => {
    // Dynamic questions mode - use currentQuestionIndex directly
    // No separate storage step in dynamic mode
    if (hasDynamicQuestions && questions.length > 0) {
      // currentQuestionIndex is 0-based, matching questions array directly
      return Math.max(0, currentQuestionIndex);
    }

    // Legacy iPhone flow
    if (isIPhone) {
      switch (currentQuestion) {
        case Question.Storage:
          return 0;
        case Question.Functionality:
          return 1;
        case Question.ScreenCondition:
          return 2;
        case Question.HousingCondition:
          return 3;
        case Question.BatteryCapacity:
          return 4;
        default:
          return 0;
      }
    } else {
      // Legacy non-iPhone flow
      const steps = getProgressSteps();
      switch (currentQuestion) {
        case Question.Storage:
          return 0;
        case Question.NetworkType:
          return shouldShowNetworkQuestion ? 1 : 0;
        case Question.Battery:
          let batteryIndex = 1;
          if (shouldShowNetworkQuestion) batteryIndex = 2;
          return batteryIndex;
        case Question.Condition:
          return steps.length - 1;
        default:
          return 0;
      }
    }
  };

  const getCurrentHeading = () => {
    // Helper: Format questionKey to readable title (e.g., "camera_condition" → "Camera condition")
    const formatKeyToTitle = (key: string): string => {
      if (!key) return 'Question';
      return key
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .replace(/^\w/, (c) => c.toUpperCase())
        .trim();
    };

    // DEBUG: Log the exact state when computing heading
    if (process.env.NODE_ENV === 'development') {
      console.log('[getCurrentHeading DEBUG]', {
        hasDynamicQuestions,
        currentQuestionIndex,
        questionsLength: questions.length,
        currentDynamicQuestion: currentDynamicQuestion ? {
          key: currentDynamicQuestion.questionKey,
          label: currentDynamicQuestion.label,
        } : null,
      });
    }

    // 1) Dynamic flow (Supabase questions)
    if (hasDynamicQuestions) {
      // PRIORITY: Check if we are currently on a dynamic question (index >= 0)
      // This MUST come before the storage check to prevent sticky storage title bug
      if (currentDynamicQuestion && currentQuestionIndex >= 0) {
        // A. Try hardcoded translations for known question types (legacy support)
        switch (currentDynamicQuestion.questionKey) {
          case 'storage':
          case 'storage_capacity':
            return {
              title: t('storageCapacityQuestion'),
              subtitle: currentDynamicQuestion.subtitle || t('selectStorageCapacity').replace(
                '{model}',
                displayNameShort
              ),
            };
          case 'screenCondition':
          case 'screen_condition':
            return {
              title: t('iPhoneScreenConditionQuestion'),
              subtitle: currentDynamicQuestion.subtitle || t('iPhoneScreenConditionSubtitle'),
            };
          case 'functionality':
            return {
              title: t('iPhoneFunctionalityQuestion').replace(
                '{model}',
                displayNameShort
              ),
              subtitle: currentDynamicQuestion.subtitle || t('iPhoneFunctionalitySubtitle'),
            };
          case 'housingCondition':
          case 'housing_condition':
            return {
              title: t('iPhoneHousingConditionQuestion'),
              subtitle: currentDynamicQuestion.subtitle || t('iPhoneHousingConditionSubtitle'),
            };
          case 'batteryCapacity':
          case 'battery_capacity':
            return {
              title: t('iPhoneBatteryCapacityQuestion'),
              subtitle: currentDynamicQuestion.subtitle || t('iPhoneBatteryCapacitySubtitle'),
            };
          case 'battery':
            return {
              title: t('batteryStatusQuestion'),
              subtitle: currentDynamicQuestion.subtitle || t('batteryStatusSubtitle'),
            };
          case 'condition':
            return {
              title: t('deviceConditionQuestion'),
              subtitle: currentDynamicQuestion.subtitle || t('deviceConditionSubtitle').replace(
                '{model}',
                displayNameShort
              ),
            };
          default:
            // B. GENERIC FALLBACK: Use label from DB, or format the key if label is missing
            // This is crucial for custom templates created in Admin Panel
            return {
              title: currentDynamicQuestion.label || formatKeyToTitle(currentDynamicQuestion.questionKey),
              subtitle: currentDynamicQuestion.subtitle || '',
            };
        }
      }

      // Storage step (index -1) - Only shown when NOT on a dynamic question
      if (currentQuestionIndex === -1) {
        return {
          title: t('storageCapacityQuestion'),
          subtitle: t('selectStorageCapacity').replace(
            '{model}',
            displayNameShort
          ),
        };
      }

      // Safety: In dynamic mode but question not loaded yet - show loading or fallback
      // This prevents falling through to legacy flow
      return {
        title: t('loading'),
        subtitle: '',
      };
    }

    // 2) Legacy flow (questions.length === 0) → eski switch(Question) mantığı
    if (isIPhone) {
      switch (currentQuestion) {
        case Question.Storage:
          return {
            title: t('storageCapacityQuestion'),
            subtitle: t('selectStorageCapacity').replace('{model}', displayNameShort),
          };
        case Question.Functionality:
          return {
            title: t('iPhoneFunctionalityQuestion').replace('{model}', displayNameShort),
            subtitle: t('iPhoneFunctionalitySubtitle'),
          };
        case Question.ScreenCondition:
          return {
            title: t('iPhoneScreenConditionQuestion'),
            subtitle: t('iPhoneScreenConditionSubtitle'),
          };
        case Question.HousingCondition:
          return {
            title: t('iPhoneHousingConditionQuestion'),
            subtitle: t('iPhoneHousingConditionSubtitle'),
          };
        case Question.BatteryCapacity:
          return {
            title: t('iPhoneBatteryCapacityQuestion'),
            subtitle: t('iPhoneBatteryCapacitySubtitle'),
          };
        default:
          return { title: '', subtitle: '' };
      }
    } else {
      switch (currentQuestion) {
        case Question.Storage:
          return {
            title: t('storageCapacityQuestion'),
            subtitle: t('selectStorageCapacity').replace('{model}', displayNameShort),
          };
        case Question.NetworkType:
          return { title: t('networkTypeQuestion'), subtitle: t('networkTypeSubtitle') };
        case Question.Battery:
          return { title: t('batteryStatusQuestion'), subtitle: t('batteryStatusSubtitle') };
        case Question.Condition:
          return {
            title: t('deviceConditionQuestion'),
            subtitle: t('deviceConditionSubtitle').replace('{model}', displayNameShort),
          };
        default:
          return { title: '', subtitle: '' };
      }
    }
  };

  // Helper to get a summary icon from the QUESTION key (spec.key === questionKey).
  // Keys are mixed NL/EN (e.g. 'batterij_conditie', 'scherm_conditie', 'behuizing',
  // 'toebehoren', 'camera', 'opladen', 'versie'). Order matters: match the specific
  // subject BEFORE the generic 'cond'/'staat' rule, otherwise 'batterij_conditie'
  // and 'scherm_conditie' would both resolve to the generic star.
  const getSpecIcon = (key: string) => {
    const k = key.toLowerCase();
    if (k.includes('versie') || k.includes('version') || k.includes('netwerk') || k.includes('network') || k.includes('wifi') || k.includes('cellular') || k.includes('sim')) return <Wifi />;
    if (k.includes('opslag') || k.includes('storage') || k.includes('capacit')) return <HardDrive />;
    if (k.includes('batterij') || k.includes('battery') || k.includes('accu') || k.includes('opladen') || k.includes('charg')) return <Battery />;
    if (k.includes('scherm') || k.includes('screen') || k.includes('display')) return <Smartphone />;
    if (k.includes('behuizing') || k.includes('housing') || k.includes('body') || k.includes('frame')) return <Shield />;
    if (k.includes('camera') || k.includes('foto') || k.includes('lens')) return <Camera />;
    if (k.includes('toebehoren') || k.includes('accessoir') || k.includes('accessor') || k.includes('extras')) return <Package />;
    if (k.includes('func') || k.includes('work') || k.includes('werk')) return <Zap />;
    if (k.includes('proc') || k.includes('cpu') || k.includes('chip')) return <Cpu />;
    if (k.includes('algemene') || k.includes('staat') || k.includes('cond')) return <Star />;
    return <Layers />;
  };

  // Generate summary of dynamic answers
  const dynamicAnswerSpecs = useMemo(
    () =>
      questions
        .filter((q) => answers[q.questionKey])
        // In dynamic mode (skipStorageStep), include storage questions in the list
        // In legacy mode, storage is handled separately by the StorageStep
        .filter(
          (q) =>
            skipStorageStep ||
            (!q.questionKey.toLowerCase().startsWith('storage') &&
              !q.questionKey.toLowerCase().includes('storage'))
        )
        .map((q) => {
          const selectedOptionKey = answers[q.questionKey];
          const selectedOption = q.options?.find(
            (opt) => opt.optionKey === selectedOptionKey
          );

          // Simplify labels for specifications list (Short & Clean)
          // Map common question keys/labels to short property names
          let shortLabel = q.label || q.questionKey;

          const lowerKey = q.questionKey.toLowerCase();
          const lowerLabel = (q.label || '').toLowerCase();

          // Map for clean short labels
          if (lowerKey === 'versie' || lowerLabel.includes('versie') || lowerLabel.includes('version')) shortLabel = t('version') || 'Versie';
          else if (lowerKey.includes('storage') || lowerLabel.includes('opslag')) shortLabel = t('storage') || 'Opslag';
          else if (lowerKey.includes('screen') || lowerLabel.includes('scherm')) shortLabel = t('screen') || 'Scherm';
          else if (lowerKey.includes('housing') || lowerLabel.includes('behuizing') || lowerLabel.includes('frame')) shortLabel = t('housing') || 'Behuizing';
          else if (lowerKey.includes('battery') || lowerLabel.includes('batterij')) shortLabel = t('battery') || 'Batterij';
          else if (lowerKey.includes('camera')) shortLabel = 'Camera';
          else if (lowerKey.includes('function') || lowerLabel.includes('functies') || lowerLabel.includes('werkt')) shortLabel = t('functionality') || 'Functies';
          else if (lowerKey.includes('condition') || lowerLabel.includes('conditie') || lowerLabel.includes('staat')) shortLabel = t('condition') || 'Conditie';
          else if (lowerKey.includes('network') || lowerLabel.includes('netwerk') || lowerLabel.includes('sim')) shortLabel = t('network') || 'Netwerk';
          else if (lowerKey.includes('icloud') || lowerLabel.includes('activation')) shortLabel = 'iCloud';
          else if (lowerKey.includes('toebehoren') || lowerKey.includes('accessor') || lowerLabel.includes('toebehoren') || lowerLabel.includes('accessor')) shortLabel = t('accessories') || 'Toebehoren';
          else if (lowerKey.includes('opladen') || lowerKey.includes('charg') || lowerLabel.includes('opladen') || lowerLabel.includes('laadt') || lowerLabel.includes('charg')) shortLabel = t('charging') || 'Opladen';
          else if (lowerKey.includes('biometr') || lowerLabel.includes('biometr')) shortLabel = t('biometrics') || 'Biometrie';

          // Vangnet: een label dat nog een hele vraag is, verliest het vraagteken.
          // Zonder dit leest de chip als "Werken alle camera's?: Ja".
          shortLabel = shortLabel.replace(/\s*\?+\s*$/, '').trim();

          // Alleen voor de versie-vraag (opslag+connectiviteit / maat+GPS) combineren
          // we label + description tot één leesbare string, bv. "128GB · Wi-Fi".
          // Andere vragen tonen alleen het label — de description daar is context
          // bij het keuze-antwoord, niet relevant voor de samenvatting.
          const isVersieQuestion = lowerKey === 'versie' || lowerLabel.includes('versie') || lowerLabel.includes('version');
          const displayValue = isVersieQuestion && selectedOption?.description
            ? `${selectedOption.label} · ${selectedOption.description}`
            : (selectedOption?.label || String(selectedOptionKey));

          return {
            key: q.questionKey,
            label: shortLabel,
            value: displayValue,
          };
        }),
    [questions, answers, modelName, t]
  );

  const heading = getCurrentHeading();

  const hasDynamicAnswers = dynamicAnswerSpecs.length > 0;

  return (
    <Container>
      <ContentArea>
        <ContentWrapper>
          {/* Main content layout */}
          <MainContent>
            {/* Inline mobile preview */}
            <InlinePreview>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                <DeviceImageWithFallback modelName={modelName} alt={displayName} imageSrc={modelImage} />
                <DeviceName style={{ margin: '0', fontSize: '0.95rem' }}>
                  {displayNameBase}
                  {displayNameMeta && <span className="model-meta">{displayNameMeta}</span>}
                </DeviceName>
              </div>
              <InlinePreviewHeading>
                <QuestionTitle>{heading.title}</QuestionTitle>
                <QuestionSubtitle style={{ marginBottom: 0 }}>{heading.subtitle}</QuestionSubtitle>
              </InlinePreviewHeading>
            </InlinePreview>

            <QuestionSection>
              <>
                {/* Progress Bar */}
                <ProgressBarContainer>
                  <ProgressStepLabel>
                    {getCompletedCount()}/{getProgressSteps().length}
                  </ProgressStepLabel>
                  <ProgressBarWrapper>
                    <ProgressBarFill progress={getProgressPercentage()} />
                  </ProgressBarWrapper>
                  <ProgressPercentLabel>
                    {getProgressPercentage()}%
                  </ProgressPercentLabel>
                </ProgressBarContainer>
                {/* Title/subtitle - hidden on mobile (shown in InlinePreview instead) */}
                <HeadingRow className="desktop-heading">
                  <div />
                  <div>
                    <QuestionTitle>{heading.title}</QuestionTitle>
                    <QuestionSubtitle>
                      {heading.subtitle}
                    </QuestionSubtitle>
                  </div>
                </HeadingRow>

                {/* ── Question content: one phase renders at a time ── */}

                {/* LOADING: questions are being fetched — skeleton UI */}
                {questionPhase === 'loading' && (
                  <QuestionContainer visible>
                    <QuestionSkeleton>
                      <SkeletonImageArea>
                        <SkeletonDeviceImage />
                      </SkeletonImageArea>
                      <SkeletonOptionsArea>
                        <SkeletonTitle />
                        <SkeletonSubtitle />
                        <SkeletonOptionsGrid>
                          <SkeletonOption />
                          <SkeletonOption />
                          <SkeletonOption />
                          <SkeletonOption />
                        </SkeletonOptionsGrid>
                      </SkeletonOptionsArea>
                    </QuestionSkeleton>
                  </QuestionContainer>
                )}

                {/* ERROR: fetch failed */}
                {questionPhase === 'error' && (
                  <QuestionContainer visible>
                    <div style={{ padding: '2rem', textAlign: 'center', color: '#ef4444' }}>
                      <p style={{ fontSize: '0.9rem' }}>{t('questionsLoadError')}</p>
                    </div>
                  </QuestionContainer>
                )}

                {/* DYNAMIC: Supabase-driven questions */}
                {questionPhase === 'dynamic' && (
                  <>
                    <QuestionStep
                      questions={questions}
                      currentQuestionIndex={currentQuestionIndex}
                      answers={answers}
                      onAnswer={(questionKey, optionKey) => {
                        updateAnswer(questionKey, optionKey);

                        // If in edit mode, return to saved position
                        if (editModeFromIndex !== null) {
                          const returnTo = editModeReturnIndex;
                          setEditModeFromIndex(null);
                          setEditModeReturnIndex(-1);
                          setTimeout(() => {
                            setCurrentQuestionIndex(returnTo);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }, 50);
                          return;
                        }

                      }}
                      onNext={handleNext}
                      onPrev={handlePrevious}
                      isFirst={currentQuestionIndex === 0}
                      isLast={currentQuestionIndex === questions.length - 1}
                      modelName={modelName}
                      imageSrc={modelImage}
                      t={t}
                      visible={currentQuestionIndex >= 0 && currentQuestionIndex < questions.length}
                    />
                  </>
                )}

                {/* LEGACY: no DB questions — Storage step + hardcoded question flow */}
                {questionPhase === 'legacy' && (
                  <>
                    {!skipStorageStep && (
                      <StorageStep
                        modelName={modelName}
                        selectedStorage={selectedStorage}
                        onSelect={handleStorageSelect}
                        t={t}
                        visible={currentQuestionIndex === -1}
                        availableStorage={availableStorage}
                        imageSrc={modelImage}
                      />
                    )}
                  </>
                )}
                {questionPhase === 'legacy' && !isComplete && (
                  <LegacyQuestionStep
                    currentQuestion={currentQuestion}
                    isIPhone={isIPhone}
                    shouldShowNetworkQuestion={shouldShowNetworkQuestion}
                    shouldShowBatteryQuestion={shouldShowBatteryQuestion}
                    selectedCondition={selectedCondition}
                    selectedNetworkType={selectedNetworkType}
                    selectedBattery={selectedBattery}
                    selectedFunctionality={selectedFunctionality}
                    selectedScreenCondition={selectedScreenCondition}
                    selectedHousingCondition={selectedHousingCondition}
                    selectedBatteryCapacity={selectedBatteryCapacity}
                    onSelectCondition={handleConditionSelect}
                    onSelectNetworkType={handleNetworkTypeSelect}
                    onSelectBattery={handleBatterySelect}
                    onSelectFunctionality={handleFunctionalitySelect}
                    onSelectScreenCondition={handleScreenConditionSelect}
                    onSelectHousingCondition={handleHousingConditionSelect}
                    onSelectBatteryCapacity={handleBatteryCapacitySelect}
                    modelName={modelName}
                    imageSrc={modelImage}
                    t={t}
                    visible={true}
                    defectsImageError={defectsImageError}
                    onDefectsImageError={() => setDefectsImageError(true)}
                  />
                )}
                {/* Battery Health Prompt Modal */}
                <BatteryHealthModal
                  isOpen={isBatteryHealthPromptOpen}
                  onClose={handleSkipBatteryHealth}
                  onSave={handleSaveBatteryHealth}
                  onSkip={handleSkipBatteryHealth}
                  batteryInputRef={batteryInputRef}
                  batteryInputValueRef={batteryInputValueRef}
                  hasBatteryModalInteracted={hasBatteryModalInteracted}
                  onInteractionChange={setHasBatteryModalInteracted}
                  t={t}
                />

                {/* Navigation aligned with helper/info box */}
                <HelperRow>
                  <div />
                  <NavigationButtons
                    canGoBack={
                      skipStorageStep
                        ? currentQuestionIndex > 0 // Dynamic mode: can go back if not on first question
                        : (currentQuestionIndex > -1 || (currentQuestionIndex === -1 && !!selectedStorage)) // Legacy mode
                    }
                    canGoNext={
                      skipStorageStep
                        ? (currentQuestionIndex < questions.length - 1) && canProceed() // Dynamic mode: no storage step
                        : (((currentQuestionIndex < questions.length - 1) || (currentQuestionIndex === -1 && questions.length > 0)) && canProceed())
                    }
                    onBack={handlePrevious}
                    onNext={handleNext}
                    t={t}
                    isLoading={isPricing}
                  />
                </HelperRow>

                {/* Inline actions and e-waste (mobile, onder de box) */}
                <InlineActions>
                  {isComplete && (
                    <>
                      <PriceDisplay
                        price={price}
                        isPricing={isPricing}
                        priceError={priceError}
                        isOnRequest={isOnRequest}
                        t={t}
                      />
                      <SellButton
                        ref={sellButtonRef}
                        data-sell-button
                        disabled={!canSell}
                        className={isSelling ? 'loading' : isPricing ? 'calculating' : ''}
                        onClick={handleSellDevice}
                      >
                        {isSelling ? (
                          <LoadingSpinner />
                        ) : isPricing ? (
                          <>{t('calculating') || 'Calculating...'}</>
                        ) : !isComplete ? (
                          <>{t('answerAllQuestions') || 'Answer all questions'}</>
                        ) : isOnRequest ? (
                          <>{t('requestOffer') || 'Offerte aanvragen'}<SellArrow><svg viewBox="0 0 24 24"><polyline points="9 5 16 12 9 19" /></svg></SellArrow></>
                        ) : hasValidPrice ? (
                          <>{t('sellFor')} €{price!.toLocaleString('nl-NL')}<SellArrow><svg viewBox="0 0 24 24"><polyline points="9 5 16 12 9 19" /></svg></SellArrow></>
                        ) : (
                          <>{t('calculating') || 'Calculating...'}</>
                        )}
                      </SellButton>
                    </>
                  )}
                  <EWasteLabel eWasteAmount={calculateEWasteSaved(deviceType, modelName)}>
                    <div className="icon">
                      <Recycle strokeWidth={2} />
                    </div>
                    <div className="content">
                      <div className="title">{t('eWasteSaving')}</div>
                      <div className="amount">
                        <span className="number">{calculateEWasteSaved(deviceType, modelName)}</span> {t('gram')}
                      </div>
                      <div className="description">{t('eWasteSavingDescription')}</div>
                    </div>
                  </EWasteLabel>
                </InlineActions>
              </>
            </QuestionSection>

            {/* Enhanced Device Preview (hidden on small screens due to inline preview) */}
            <DevicePreview>


              <DeviceSpecsContainer>
                <DeviceSpecsHeader>
                  <SpecBadge>
                    <SlidersHorizontal size={14} />
                    {t?.('properties') || 'EIGENSCHAPPEN'}
                  </SpecBadge>

                  <DeviceIdentityCard>
                    <HeroImage>
                      <DeviceImageWithFallback
                        modelName={modelName}
                        alt={displayName}
                        className="hero-image"
                        imageSrc={modelImage}
                      />
                    </HeroImage>

                    <HeroTitle>
                      {displayNameBase}
                      {displayNameMeta && <span className="model-meta">{displayNameMeta}</span>}
                    </HeroTitle>
                  </DeviceIdentityCard>

                  <SpecificationExplainer>
                    <span>{t('chosenSpecs')}</span>
                  </SpecificationExplainer>
                </DeviceSpecsHeader>

                <SpecsList>
                  {/* Legacy storage spec - only shown when not in dynamic mode */}
                  {/* In dynamic mode, storage is part of dynamicAnswerSpecs */}
                  {!skipStorageStep && selectedStorage && (
                    <SpecItem
                      clickable={currentQuestionIndex !== -1}
                      active={currentQuestionIndex === -1 && editModeFromIndex === null}
                      onClick={() => {
                        if (currentQuestionIndex !== -1) {
                          setEditModeReturnIndex(currentQuestionIndex);
                          setEditModeFromIndex(-1);
                          setCurrentQuestionIndex(-1);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }
                      }}
                      blurred={editModeFromIndex !== null && editModeFromIndex >= 0}
                    >
                      <SpecIconWrapper $active={currentQuestionIndex === -1 && editModeFromIndex === null}>
                        <HardDrive />
                      </SpecIconWrapper>
                      <SpecContent>
                        <SpecLabel>{t('storage')}</SpecLabel>
                        <SpecValue>{selectedStorage}</SpecValue>
                      </SpecContent>
                    </SpecItem>
                  )}
                  {dynamicAnswerSpecs.map((spec, index) => {
                    // Normalize lookup of original index
                    const originalQuestionIndex = questions.findIndex(q => q.questionKey === spec.key);

                    // Determine states based on edit mode
                    const isActive = currentQuestionIndex === originalQuestionIndex;

                    // Blur logic:
                    let isBlurred = false;
                    if (editModeFromIndex !== null) {
                      // Edit mode: blur ALL items except the one being edited
                      isBlurred = originalQuestionIndex !== editModeFromIndex;
                    } else {
                      // Normal flow: blur future items only if we're still in the questionnaire
                      const isInProgress = currentQuestionIndex < questions.length && currentQuestionIndex >= -1;
                      isBlurred = isInProgress && originalQuestionIndex > currentQuestionIndex;
                    }

                    // Show checkmark for completed (answered) items that are not active or blurred
                    const showCheck = !isBlurred && !isActive && originalQuestionIndex < currentQuestionIndex;

                    return (
                      <SpecItem
                        key={spec.key}
                        clickable={!isBlurred && !isActive}
                        active={isActive}
                        blurred={isBlurred}
                        onClick={() => {
                          if (!isBlurred && !isActive) {
                            setEditModeReturnIndex(currentQuestionIndex);
                            setEditModeFromIndex(originalQuestionIndex);
                            setCurrentQuestionIndex(originalQuestionIndex);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }
                        }}
                      >
                        <SpecIconWrapper $active={isActive}>
                          {getSpecIcon(spec.key)}
                        </SpecIconWrapper>
                        <SpecContent>
                          <SpecLabel>{spec.label}</SpecLabel>
                          <SpecValue title={spec.value}>{spec.value}</SpecValue>
                        </SpecContent>
                      </SpecItem>
                    );
                  })}
                </SpecsList>


              </DeviceSpecsContainer>

              {isComplete ? (
                <PriceDisplay
                  price={price}
                  isPricing={isPricing}
                  priceError={priceError}
                  isOnRequest={isOnRequest}
                  t={t}
                />
              ) : null}

              <SellButton
                ref={sellButtonRef}
                data-sell-button
                disabled={!canSell}
                className={isSelling ? 'loading' : isPricing ? 'calculating' : ''}
                onClick={handleSellDevice}
              >
                {isSelling ? (
                  <LoadingSpinner />
                ) : isPricing ? (
                  <>{t('calculating') || 'Calculating...'}</>
                ) : !isComplete ? (
                  <>{t('answerAllQuestions') || 'Answer all questions'}</>
                ) : isOnRequest ? (
                  <>{t('requestOffer') || 'Offerte aanvragen'}<SellArrow><svg viewBox="0 0 24 24"><polyline points="9 5 16 12 9 19" /></svg></SellArrow></>
                ) : hasValidPrice ? (
                  <>{t('sellFor')} €{price!.toLocaleString('nl-NL')}<SellArrow><svg viewBox="0 0 24 24"><polyline points="9 5 16 12 9 19" /></svg></SellArrow></>
                ) : (
                  <>{t('calculating') || 'Calculating...'}</>
                )}
              </SellButton>

              <EWasteLabel eWasteAmount={calculateEWasteSaved(deviceType, modelName)}>
                <div className="icon">
                  <Recycle strokeWidth={2} />
                </div>
                <div className="content">
                  <div className="title">{t('eWasteSaving')}</div>
                  <div className="amount">
                    <span className="number">{calculateEWasteSaved(deviceType, modelName)}</span> {t('gram')}
                  </div>
                  <div className="description">{t('eWasteSavingDescription')}</div>
                </div>
              </EWasteLabel>
            </DevicePreview>
          </MainContent>
        </ContentWrapper>
      </ContentArea>
    </Container>
  );
};

export default DeviceSpecifications; 